import { z } from 'zod';
import type { QuotaCategory, QuotaScope } from '../schemas/storage-quota';
import {
  isValidItalianVat,
  isValidItalianFiscalCode,
  isValidIban,
  isValidSdiCode,
  isValidEmail,
} from '../validators/italian-fiscal';
import type { PosProductVariantAttributes } from '../pos/admin-inputs';
import type {
  SnapshotStatusDistEntry,
} from '../pos/inventory-responses';
import { TenantProductSchema } from '../products';
import { supplierInvoiceIdentityStatusSchema } from '../schemas/supplier-invoices';
import { AcubeCompanyConfigSchema } from '../fiscal/acube';

// API Response wrapper
export const ApiResponseSchema = z.object({
  success: z.boolean(),
  data: z.unknown().optional(),
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional()
  }).optional(),
  meta: z.object({
    timestamp: z.date(),
    request_id: z.string().uuid(),
    tenant_id: z.string().uuid().optional()
  })
});

// NOTE: `T = any` mantenuto come default intenzionale per back-compat.
// Cambiarlo a `unknown` propaga ~450 narrow-error nei consumer FE che fanno
// `res.data.foo` senza guard. Migrare consumer-by-consumer in task dedicato
// prima di stringere il default. Vedi task #1631 (Round 2 generics).
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-esistente senza motivazione, descrizione aggiunta meccanicamente (#3359)
export type ApiResponse<T = any> = {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
  meta: {
    timestamp: Date;
    request_id: string;
    tenant_id?: string;
  };
};

// Pagination schema
export const PaginationSchema = z.object({
  page: z.number().min(1).default(1),
  limit: z.number().min(1).max(1000).default(50),
  total: z.number().min(0),
  total_pages: z.number().min(0)
});

export type Pagination = z.infer<typeof PaginationSchema>;

// JWT payload schema
export const JWTPayloadSchema = z.object({
  sub: z.string().uuid(), // user_id (standard JWT claim)
  id: z.string().uuid().optional(), // Alternative user_id field (used in backend)
  email: z.string().email().optional(),
  tenant_id: z.string().uuid().optional(),
  tenant_slug: z.string().optional(),
  store_id: z.string().uuid().optional(),
  sip_domain: z.string().optional(),
  role: z.enum(['super_admin', 'admin', 'manager', 'user', 'agent']),
  role_id: z.string().uuid().optional(), // RBAC custom role ID
  permissions: z.array(z.string()).optional(),
  // dispatch lookup schema (`master.*` vs `public.*`) per la
  // catena auth (token_valid_after, refresh_tokens, role_permissions).
  // Tutti i nuovi JWT lo emettono. Manca solo nei token in flight pre-deploy;
  // back-compat: middleware infer 'tenant' se assente (e usa is_master_tenant
  // legacy per il bypass `super_admin`).
  subject_kind: z.enum(['master', 'tenant', 'brand']).optional(),
  // prodotto/edition del tenant (`tenants.product`). Emesso da
  // login+refresh tenant; assente nei token master e nei token in flight
  // pre-deploy (back-compat). Tenuto `string` (non enum) per non invalidare
  // token legittimi in caso di prodotti futuri: la fonte di verità resta il DB.
  product: z.string().optional(),
  is_master_tenant: z.boolean().optional(), // True if user belongs to master tenant (edgvoip)
  is_impersonating: z.boolean().optional(), // True if this is an impersonation session
  impersonated_by: z.string().uuid().optional(), // Master admin user ID who initiated impersonation
  actor_sub: z.string().uuid().optional(), // Original master admin sub for audit (immutable during impersonation)
  actor_role: z.string().optional(), // Original master admin role
  actor_is_master: z.boolean().optional(), // Original master admin is_master flag
  // claim standard issuer/audience sui JWT interni HS256.
  // Emessi da tutti i nuovi access token (tenant e master); assenti nei
  // token in flight pre-deploy (back-compat, verifica tollerante nel
  // middleware finché la finestra legacy non si chiude).
  iss: z.string().optional(),
  aud: z.string().optional(),
  // Brand access tokens use millisecond precision so a reset/deactivation can
  // invalidate a token issued earlier in the same Unix second.
  issued_at_ms: z.number().int().nonnegative().optional(),
  // Brand-only, lossless PostgreSQL timestamptz authentication epoch. Brand
  // middleware requires an exact live DB match; legacy Brand tokens re-login.
  brand_auth_epoch: z.string().optional(),
  iat: z.number(),
  exp: z.number()
});

export type JWTPayload = z.infer<typeof JWTPayloadSchema>;

// i campi nominativi (Ragione sociale, Nome, Cognome) sono normalizzati
// in UPPERCASE lato Zod. Transform non bypassabile via API: qualsiasi payload
// inserito tramite questo schema viene persistito in maiuscolo. Esclusi: email,
// password, codice fiscale, P.IVA, IBAN, indirizzi, note libere.
const upperName = (max: number) =>
  z.string().min(1).max(max).transform((v) => v.toLocaleUpperCase('it-IT'));

// Riconciliazione dei tre enum "provider" fiscali, che sono assi
// DISTINTI e ortogonali ("fattura elettronica ⊥ registratore"):
//   1. Fattura elettronica / SdI → `EInvoiceProvider` = 'acube' | 'fic'
//      (backend: `InvoiceProvider`). Provider che trasmette le fatture allo SdI.
//   2. Driver scontrino/registratore per-RS → `FiscalProviderName`
//      = 'none' | 'acube' | 'epson' | 'rch' | 'custom'. Hardware/cloud che
//      batte gli scontrini RT. NON include 'fic'.
//   3. Sezionale → `SezionaleProvider` = 'acube' | 'fic' | 'internal'
//      = `EInvoiceProvider` ∪ 'internal' (documenti logistici/commerciali).
// Una RS può emettere fatture via FIC e scontrini via Epson: i due assi non si
// implicano. Il provider di fattura elettronica per-RS vive in
// `fiscal_provider_config.einvoice_provider` quando il driver scontrino non lo
// implica ('acube' implica einvoice='acube'). Risoluzione capability-aware in
// backend `fiscal-provider-resolver.service.ts`.
export const EINVOICE_PROVIDERS = ['acube', 'fic'] as const;
export type EInvoiceProvider = (typeof EINVOICE_PROVIDERS)[number];

// Stati SdI normalizzati cross-provider. Ogni provider (A-Cube,
// Fatture in Cloud) mappa i propri stati raw su questo enum interno unico;
// lo stato raw del provider viene conservato in `marking_timeline`
// (payload_summary.provider_raw_status), MAI usato come fonte di stato.
// Semantica:
//   - submitted:  inviato allo SdI, esito non ancora noto
//   - delivered:  consegnata al destinatario (RC)
//   - not_delivered: SdI ok ma mancata consegna (MC — resa disponibile in AdE)
//   - accepted:   accettata (PA: EC positivo; B2B: consegna/decorrenza)
//   - rejected:   scartata dallo SdI (NS) — stato terminale, richiede riemissione
//   - error:      errore di trasmissione lato provider (pre-SdI)
export const SDI_STATUSES = [
  'submitted',
  'delivered',
  'not_delivered',
  'accepted',
  'rejected',
  'error',
] as const;
export type SdiStatus = (typeof SDI_STATUSES)[number];
/** Stati terminali: il reconciler smette di pollare il documento. */
export const SDI_TERMINAL_STATUSES: ReadonlyArray<SdiStatus> = ['delivered', 'accepted', 'rejected'];

// Provider fiscale per-azienda (multi-RS). Discriminated union con config
// tipizzata per-driver. `none` = nessun emitter configurato (la company esiste
// solo a livello anagrafico). `einvoice_provider`: provider di fattura
// elettronica esplicito per-RS, indipendente dal driver scontrino. Se assente:
// 'acube' implica einvoice='acube'; gli altri driver senza questo campo = nessuna
// capability fattura elettronica. Il ramo acube vive in `fiscal/acube.ts`.
export const FiscalProviderConfigSchema = z.discriminatedUnion('provider', [
  z.object({
    provider: z.literal('none'),
    einvoice_provider: z.enum(['acube', 'fic']).optional(),
    fic_company_id: z.number().int().positive().optional(),
  }),
  AcubeCompanyConfigSchema,
  z.object({
    provider: z.literal('epson'),
    printer_serial: z.string().min(1).optional(),
    host: z.string().min(1).optional(),
    einvoice_provider: z.enum(['acube', 'fic']).optional(),
    fic_company_id: z.number().int().positive().optional(),
  }),
  z.object({
    provider: z.literal('rch'),
    printer_serial: z.string().min(1).optional(),
    host: z.string().min(1).optional(),
    einvoice_provider: z.enum(['acube', 'fic']).optional(),
    fic_company_id: z.number().int().positive().optional(),
  }),
  z.object({
    provider: z.literal('custom'),
    printer_serial: z.string().min(1).optional(),
    host: z.string().min(1).optional(),
    einvoice_provider: z.enum(['acube', 'fic']).optional(),
    fic_company_id: z.number().int().positive().optional(),
  }),
]);
export type FiscalProviderConfig = z.infer<typeof FiscalProviderConfigSchema>;
export type FiscalProviderName = FiscalProviderConfig['provider'];

// Company schema for tenant management. rimossi `is_primary`
// (multi-RS pari grado) + colonne IT duplicate, aggiunti `fiscal_provider`
// e `fiscal_provider_config` (discriminated union).
export const CompanySchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  legal_name: upperName(255),
  vat_number: z.string().max(50).optional(),
  tax_code: z.string().max(50).optional(),
  address: z.string().optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(100).optional(),
  postal_code: z.string().max(20).optional(),
  country: z.string().max(100).default('Italy'),
  fiscal_provider: z.enum(['none', 'acube', 'epson', 'rch', 'custom']).default('none'),
  fiscal_provider_config: FiscalProviderConfigSchema.default({ provider: 'none' }),
  // Logo RS: chiave R2 (mai URL pubblico), servito via endpoint asset presigned.
  logo_url: z.string().max(500).nullable().optional(),
  // governance brand: org che gestisce la RS (NULL = tenant-managed). Sola lettura.
  managed_by_org_id: z.string().uuid().nullable().optional(),
  created_at: z.date(),
  updated_at: z.date()
});

export type Company = z.infer<typeof CompanySchema>;

// Input schema per CRUD `/api/tenant/companies` (campi gestibili dal FE).
export const CompanyInputSchema = z.object({
  legal_name: upperName(255),
  vat_number: z.string().max(50).optional(),
  tax_code: z.string().max(50).optional(),
  address: z.string().optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(100).optional(),
  postal_code: z.string().max(20).optional(),
  country: z.string().max(100).default('Italy'),
  subject_type: z.enum(['company', 'sole_proprietorship', 'freelancer']).optional(),
  first_name: z.string().max(100).optional(),
  last_name: z.string().max(100).optional(),
  legal_form: z.string().max(50).optional(),
  tax_regime: z.string().max(20).optional(),
  sdi_code: z.string().max(50).optional(),
  pec_email: z.string().email().optional().or(z.literal('')),
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().max(50).optional(),
  rea_office: z.string().max(10).optional(),
  rea_number: z.string().max(30).optional(),
  share_capital: z.union([z.number(), z.string()]).optional(),
  share_capital_paid_up: z.union([z.number(), z.string()]).optional(),
  stato_liquidazione: z.enum(['LS', 'LN']).optional(),
  socio_unico: z.enum(['SU', 'SM']).optional(),
  fattura_pa_destinazione: z.string().max(20).optional(),
  representative_fiscal_code: z.string().max(50).optional(),
  conservazione_enabled: z.boolean().optional(),
  fiscal_provider: z.enum(['none', 'acube', 'epson', 'rch', 'custom']).optional(),
  fiscal_provider_config: FiscalProviderConfigSchema.optional(),
});
export type CompanyInput = z.infer<typeof CompanyInputSchema>;

// B2B Contact schema (referente di un cliente B2B = crm_b2b_contacts).
// il referente aggancia un cliente B2B via customer_id, non più
// una ragione sociale tenant (companies).
export const TenantContactSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  customer_id: z.string().uuid(),
  first_name: upperName(100),
  last_name: upperName(100),
  role: z.string().max(100).optional(),
  email: z.string().email().optional(),
  phone: z.string().max(50).optional(),
  mobile: z.string().max(50).optional(),
  is_primary: z.boolean().default(false),
  // identità Telegram (chat_id catturato dal dispatcher inbound,
  // username editabile) + recapiti social.
  telegram_chat_id: z.string().nullable().optional(),
  telegram_username: z.string().max(100).nullable().optional(),
  instagram: z.string().max(255).nullable().optional(),
  tiktok: z.string().max(255).nullable().optional(),
  facebook: z.string().max(255).nullable().optional(),
  created_at: z.date(),
  updated_at: z.date()
});

export type TenantContact = z.infer<typeof TenantContactSchema>;

export const PASSWORD_POLICY = {
  minLength: 12,
  maxLength: 100,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecial: true,
  specialChars: '!@#$%^&*()_+-=[]{}|;:,.<>?/~`',
};

export function validatePasswordPolicy(password: string): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (password.length < PASSWORD_POLICY.minLength) errors.push(`Minimo ${PASSWORD_POLICY.minLength} caratteri`);
  if (password.length > PASSWORD_POLICY.maxLength) errors.push(`Massimo ${PASSWORD_POLICY.maxLength} caratteri`);
  if (PASSWORD_POLICY.requireUppercase && !/[A-Z]/.test(password)) errors.push('Almeno una lettera maiuscola');
  if (PASSWORD_POLICY.requireLowercase && !/[a-z]/.test(password)) errors.push('Almeno una lettera minuscola');
  if (PASSWORD_POLICY.requireNumber && !/[0-9]/.test(password)) errors.push('Almeno un numero');
  if (PASSWORD_POLICY.requireSpecial && !/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/.test(password)) errors.push('Almeno un carattere speciale (!@#$%^&*...)');
  return { valid: errors.length === 0, errors };
}

// Admin User schema for tenant creation
export const AdminUserSchema = z.object({
  first_name: upperName(100),
  last_name: upperName(100),
  email: z.string().email(),
  password: z.string()
    .min(PASSWORD_POLICY.minLength, `La password deve avere almeno ${PASSWORD_POLICY.minLength} caratteri`)
    .max(PASSWORD_POLICY.maxLength)
    .regex(/[A-Z]/, 'La password deve contenere almeno una lettera maiuscola')
    .regex(/[a-z]/, 'La password deve contenere almeno una lettera minuscola')
    .regex(/[0-9]/, 'La password deve contenere almeno un numero')
    .regex(/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/, 'La password deve contenere almeno un carattere speciale'),
  role: z.enum(['admin']).default('admin')
});

export type AdminUser = z.infer<typeof AdminUserSchema>;

// Create Tenant Request schema
export const CreateTenantRequestSchema = z.object({
  name: upperName(255),
  slug: z.string().min(1).max(100).regex(/^[a-z0-9-]+$/),
  domain: z.string().min(1).max(255),
  sip_domain: z.string().min(1).max(255),
  admin_user: AdminUserSchema,
  companies: z.array(CompanyInputSchema).min(1),
  modules: z.record(z.string(), z.boolean()).optional(),
  // prodotto/edition (default 'easyflow'); determina il profilo
  // moduli seminato in tenant_modules alla creazione (PRODUCT_REGISTRY).
  product: TenantProductSchema.optional(),
  settings: z.object({
    max_extensions: z.number().int().min(1).max(10000).optional(),
    max_trunks: z.number().int().min(1).max(1000).optional(),
    max_concurrent_calls: z.number().int().min(1).max(1000).optional(),
    max_users: z.number().int().min(1).max(10000).optional(),
    max_ai_agents: z.number().int().min(1).max(1000).optional(),
    recording_enabled: z.boolean().optional(),
    gdpr_compliant: z.boolean().optional(),
    voicemail_directory: z.string().optional(),
  }).optional(),
});

export type CreateTenantRequest = z.infer<typeof CreateTenantRequestSchema>;

// PUT /api/tenants/:id — gate Zod sui nominativi (name, legal_name).
// Tutti i campi opzionali (update parziale). passthrough() per non rompere
// payload con altri campi (settings, modules, ecc.) gestiti dal service.
export const UpdateTenantRequestSchema = z.object({
  name: upperName(255).optional(),
  companies: z
    .array(
      z
        .object({
          legal_name: upperName(255).optional(),
        })
        .passthrough(),
    )
    .optional(),
}).passthrough();
export type UpdateTenantRequest = z.infer<typeof UpdateTenantRequestSchema>;

// Cross-tenant analytics schemas
export const CrossTenantStatsSchema = z.object({
  total_tenants: z.number(),
  total_users: z.number(),
  total_extensions: z.number(),
  total_calls_24h: z.number(),
  active_tenants: z.number(),
  inactive_tenants: z.number()
});

export type CrossTenantStats = z.infer<typeof CrossTenantStatsSchema>;

export const TenantStatsSchema = z.object({
  tenant_id: z.string().uuid(),
  tenant_name: z.string(),
  tenant_slug: z.string(),
  // prodotto/edition del tenant per segmentazione cross-tenant.
  tenant_product: z.string().nullable(),
  users_count: z.number(),
  extensions_count: z.number(),
  calls_24h: z.number(),
  companies_count: z.number(),
  contacts_count: z.number(),
  status: z.string()
});

export type TenantStats = z.infer<typeof TenantStatsSchema>;

// ============================================================================
// Payload canonico per PUT /api/ai-tools/service-models/:serviceKey.
// `provider` resta opzionale per 1 release (back-compat rolling deploy
// FE-1/BE-1/BE-2): se assente, il backend deriva da tenant_service_routing /
// platform_model_config e risponde con header `Deprecation: true` + `Sunset`.
// Il follow-up cleanup-task rende `provider` required + rimuove la derivazione.
// ============================================================================
export const ServiceModelUpdatePayloadSchema = z.object({
  provider: z.string().min(1).optional(),
  model: z.string().min(1),
}).strict();
export type ServiceModelUpdatePayload = z.infer<typeof ServiceModelUpdatePayloadSchema>;

// ============================================================================
// Cifratura credenziali SIP trunk a riposo.
// ============================================================================

/**
 * Credenziali sensibili di un SIP trunk. Memorizzate cifrate AES-256-GCM
 * nella colonna dedicata `sip_trunks.sip_credentials_encrypted` (mai in
 * chiaro nel JSONB `sip_config`, mai serializzate in response API).
 */
export interface SipTrunkCredentials {
  username: string;
  password: string;
  /** Token bearer provider (es. trunk SIP cloud). */
  auth_token?: string;
  /** API key provider (es. integrazioni REST per signaling). */
  api_key?: string;
}

/**
 * Sip config "public": stessa shape di `sip_trunks.sip_config` (host, port,
 * transport, register, codec_prefs, ecc.) ma SENZA password/auth_token/api_key.
 * Username resta esposto in quanto identifier non segreto, riusato per DID e
 * caller-id da numerosi consumer downstream (status.service, sip-health-monitor,
 * voip-routes).
 */
export interface SipTrunkConfigPublic {
  host?: string;
  port?: number;
  username?: string;
  transport?: 'udp' | 'tcp' | 'tls';
  register?: boolean;
  realm?: string;
  from_user?: string;
  from_domain?: string;
  register_proxy?: string;
  register_transport?: 'udp' | 'tcp' | 'tls';
  retry_seconds?: number;
  caller_id_in_from?: boolean;
  contact_params?: string;
  ping?: boolean;
  ping_time?: number;
  auth_realm?: string;
  [key: string]: unknown;
}

/**
 * Config SIP pubblica del registrar/proxy verso il SBC, identica per tutto il
 * tenant (il dominio/realm `<slug>.easyflowai.it` è l'unica parte per-tenant,
 * derivabile dallo slug lato consumer). Fonte di verità: `getSipPublicConfig()`
 * (env `SIP_PUBLIC_*`). Esposta da `/sip-public-config` e consumata dalla UI
 * di provisioning + wizard. Nessun apex Cloudflare-proxied: `sipServer` è il
 * registrar SIP raggiungibile (`sip.easyflowai.it`), non `easyflowai.it`.
 */
export interface SipPublicConfig {
  wssUrl: string;
  sipServer: string;
  sipPortUdp: number;
  sipPortTls: number;
  sipPortWss: number;
  outboundProxy: string;
}

/**
 * Response API completa di un SIP trunk visibile al frontend (admin tenant,
 * master cross-tenant, api-v2). Le credenziali sono mascherate via placeholder
 * `••••••` + flag booleano `has_credentials` per discriminare trunk con/senza
 * credenziali memorizzate. `sip_credentials_encrypted` non viene mai esposto.
 */
export interface SipTrunkPublicView {
  sip_config: SipTrunkConfigPublic;
  has_credentials: boolean;
  credentials_masked: string;
}

// ============================================================================
// Contratto tipizzato dei campi configurazione delle Sedi (stores).
// `settings` rispecchia `StoreSettings` del backend (store.service.ts);
// `sip_trunk_settings` è la config SIP trunk pubblica (nessuna credenziale).
// ============================================================================

/** Impostazioni generiche di una sede. JSONB `stores.settings`. */
export interface StoreSettings {
  timezone?: string;
  business_hours?: {
    open: string;
    close: string;
    days: string[];
  };
  outbound_caller_id?: string | null;
  recording_consent_required?: boolean;
  [k: string]: unknown;
}

/**
 * Impostazioni SIP trunk associate alla sede. Stessa shape pubblica di
 * `SipTrunkConfigPublic` (host/port/transport/register/…), mai credenziali.
 */
export type StoreSipTrunkSettings = SipTrunkConfigPublic;

// ============================================================================
// Product lifecycle + point-in-time snapshot DTOs.
// ============================================================================
// Discriminated union (`event_type`) coprente le 5 sorgenti che concorrono al
// lifecycle di un prodotto:
//   - movement           → `pos_stock_movements` (carico, scarico, transfer,
//                          return, scrap)
//   - status_change      → `pos_product_status_log` (transizione di lifecycle)
//   - serial_event       → cambi di stato di `pos_product_serials`
//                          (available → reserved/sold/transferred/scrapped)
//   - inventory_adjustment → movimento con `movement_type='inventory'` o
//                          `reference_type='inventory_session'`
//   - alert              → `pos_stock_alerts` (sotto soglia, scaduto, ecc.)
export const LIFECYCLE_EVENT_TYPES = [
  'movement', 'status_change', 'serial_event', 'inventory_adjustment', 'alert',
] as const;

export type LifecycleEventType = typeof LIFECYCLE_EVENT_TYPES[number];

export interface LifecycleBaseEvent {
  event_type: LifecycleEventType;
  /** Identificativo univoco dell'evento dentro la propria sorgente. */
  event_id: string;
  /** Timestamp evento (ISO8601). */
  at: string;
  warehouse_id: string | null;
  warehouse_name: string | null;
  lot_id: string | null;
  lot_number: string | null;
  serial_id: string | null;
  serial_number: string | null;
  reference_type: string | null;
  reference_id: string | null;
  /** Identificativo dell'utente che ha generato l'evento (nullable per
   *  alert/system). */
  actor_id: string | null;
  /** Nome leggibile dell'utente — first+last name oppure email come fallback. */
  actor_name: string | null;
  notes: string | null;
}

export interface LifecycleMovementEvent extends LifecycleBaseEvent {
  event_type: 'movement';
  movement_type: string;
  /** Variazione netta firmata della giacenza in unità prodotto. Sempre
   *  valorizzata: per righe storiche pre-cutover #1351 viene derivata via
   *  fallback da `movement_type` + quantità assoluta. */
  quantity_signed: number;
  to_warehouse_id: string | null;
  to_warehouse_name: string | null;
  unit_cost: number | null;
}

/**
 * Stato logistico canonico di una posizione di magazzino.
 * Enum Postgres reale `product_logistic_status` (14 valori EN). Materializzato
 * come bucket su `pos_stock.logistic_status`; la disponibilita' di vendita
 * coincide con il bucket `in_stock`. Lo stato dei seriali deriva invece da
 * `pos_product_serial_units.status`.
 */
export type ProductLogisticStatus =
  | 'in_stock'
  | 'reserved'
  | 'preparing'
  | 'shipping'
  | 'delivered'
  | 'sold'
  | 'customer_return'
  | 'doa_return'
  | 'in_service'
  | 'supplier_return'
  | 'in_transfer'
  | 'lost'
  | 'damaged'
  | 'internal_use';

export const PRODUCT_LOGISTIC_STATUSES: readonly ProductLogisticStatus[] = [
  'in_stock', 'reserved', 'preparing', 'shipping', 'delivered', 'sold',
  'customer_return', 'doa_return', 'in_service', 'supplier_return',
  'in_transfer', 'lost', 'damaged', 'internal_use',
];

/** Etichette IT canoniche per i 14 stati logistici (UI inventario / picker). */
export const PRODUCT_LOGISTIC_STATUS_LABELS_IT: Readonly<Record<ProductLogisticStatus, string>> = {
  in_stock: 'In giacenza',
  reserved: 'Riservato',
  preparing: 'In preparazione',
  shipping: 'In spedizione',
  delivered: 'Consegnato',
  sold: 'Venduto',
  customer_return: 'Reso cliente',
  doa_return: 'Reso DOA',
  in_service: 'In assistenza',
  supplier_return: 'Reso fornitore',
  in_transfer: 'In trasferimento',
  lost: 'Smarrito',
  damaged: 'Danneggiato',
  internal_use: 'Uso interno',
};

export interface LifecycleStatusEvent extends LifecycleBaseEvent {
  event_type: 'status_change';
  from_status: string | null;
  to_status: string;
  quantity: number | null;
  cost_unit: number | null;
  metadata: Record<string, unknown>;
}

export interface LifecycleSerialEvent extends LifecycleBaseEvent {
  event_type: 'serial_event';
  serial_status_from: string | null;
  serial_status_to: string;
  movement_id: string | null;
}

export interface LifecycleInventoryAdjustmentEvent extends LifecycleBaseEvent {
  event_type: 'inventory_adjustment';
  delta: number;                 // signed
  quantity_before: number | null;
  quantity_after: number | null;
  adjustment_reason: string | null;
  session_id: string | null;
}

export interface LifecycleAlertEvent extends LifecycleBaseEvent {
  event_type: 'alert';
  /** Categoria dell'alert: `low_stock`, `out_of_stock`, `expired`, ... */
  alert_kind: string;
  severity: 'info' | 'warning' | 'critical';
  resolved_at: string | null;
}

export type LifecycleEvent =
  | LifecycleMovementEvent
  | LifecycleStatusEvent
  | LifecycleSerialEvent
  | LifecycleInventoryAdjustmentEvent
  | LifecycleAlertEvent;

export interface LifecyclePage {
  events: LifecycleEvent[];
  next_cursor: string | null;
  cutover_warning: string | null;
}

export interface LifecycleQueryParams {
  warehouse_id?: string | null;
  lot_id?: string | null;
  serial_id?: string | null;
  from?: string | null;
  to?: string | null;
  event_types?: LifecycleEventType[] | null;
  movement_types?: string[] | null;
  limit?: number;
  cursor?: string | null;
}

// --- Snapshot ---------------------------------------------------------------

export interface SnapshotBreakdownEntry {
  warehouse_id: string;
  warehouse_name: string;
  /** codice magazzino (pos_warehouses.code) per la "Distribuzione per Magazzino" del modale. */
  warehouse_code: string | null;
  lot_id: string | null;
  lot_number: string | null;
  qty: number;
  reserved: number;
  available: number;
  cmp: number | null;
  /** Null when one or more residual layers lack an immutable cost snapshot. */
  value_cmp: number | null;
  value_sell: number;
  /** soglie reali per-magazzino (pos_stock) per lo stato di scorta. */
  reorder_point: number;
  min_stock_level: number;
}

export interface SnapshotSerialEntry {
  serial_id: string;
  serial_number: string;
  /** Categoria seriale (pos_product_serials.kind): imei/iccid/macaddr/serial. */
  kind: SerialKind | null;
  status: string;
  warehouse_id: string | null;
  warehouse_name: string | null;
  lot_id: string | null;
  unit_cost: number | null;
}

/** Historical cost statistics returned by the mounted product snapshot route. */
export interface ProductCostStatsGlobal {
  cmp_global: number | null;
  min_cost: number | null;
  max_cost: number | null;
  last_cost: number | null;
  avg_supplier_price: number | null;
}

export interface ProductSnapshotProduct {
  id: string;
  sku: string;
  ean: string | null;
  name: string;
  description: string | null;
  image_url: string | null;
  category_name: string | null;
  brand_name: string | null;
  supplier_name: string | null;
  serial_mode: 'none' | 'unique' | 'non_unique';
  is_serialized: boolean;
  reorder_point: number;
  min_stock_level: number;
  max_stock_level: number;
  cost_price: number | null;
  sell_price: number | null;
  product_type_name: string | null;
  /**
   * natura prodotto reale (pos_products.product_nature): TIPO PRODOTTO.
   * NB: Memoria/Colore/Condizione NON esistono come colonne discrete (modello
   * variante "Piano B" non a schema) → sono surfacate solo via `variant_info`.
   */
  product_nature: 'product' | 'service' | 'canvas';
  product_status: 'active' | 'inactive' | 'discontinued' | 'draft';
  variant_info: string | null;
}

/**
 * riconciliazione ledger vs giacenza materializzata.
 * `ledger_qty` è ricostruito da `pos_stock_movements` con l'espressione di
 * segno canonica (`sqlSignedDeltaExpr`) + fallback #1351 per le righe senza
 * `delta_signed`; `materialized_qty` è la giacenza `pos_stock` (= `total_qty`
 * dello snapshot, scoped come lo snapshot). `discrepancy = ledger − materiale`.
 */
export interface SnapshotReconciliation {
  /** Giacenza ricostruita dal ledger (Σ delta firmata ≤ as_of, scoped). */
  ledger_qty: number;
  /** Giacenza materializzata (`pos_stock`), = `total_qty` dello snapshot. */
  materialized_qty: number;
  /** `ledger_qty − materialized_qty`. 0 = riconciliato. */
  discrepancy: number;
  /** True se la ricostruzione ha usato il fallback #1351 (righe `delta_signed` NULL nel range). */
  used_legacy_fallback: boolean;
  /** Quantità in stati logistici non disponibili (≠ `in_stock`), = `total_qty − total_available`. */
  unavailable_qty: number;
}

// ----------------------------------------------------------------------------
// breakdown matrice magazzino × stato logistico per-prodotto,
// caricato on-demand all'espansione di una riga della vista Inventario
// aggregata. Deriva da `pos_stock` (GROUP BY warehouse_id, logistic_status).
// La shape Zod canonica e il tipo inferito sono in
// `pos/inventory-responses.ts`; il tipo importato sopra mantiene qui la
// compatibilità con i contratti snapshot già presenti.
// ----------------------------------------------------------------------------

// ============================================================================
// RAG Multimodal Scrape: estrazione e indicizzazione immagini
// inline da pagine web durante l'ingest URL della knowledge base.
// ============================================================================

/** Motivi per cui un candidato media viene scartato durante lo scrape. */
export const MEDIA_SKIP_REASONS = [
  'data_uri',           // src="data:..." — non scaricabile in modo sicuro
  'invalid_url',        // URL non parsabile o relativo non risolvibile
  'non_http',           // schema diverso da http/https (es. blob:, ftp:)
  'parse_no_src',       // tag <img> senza attributo src
  'decorative',         // pattern URL chiaramente decorativi (favicon/spacer/...)
  'host_blacklist',     // hostname di CDN tracker noto
  'mime_unsupported',   // extension/Content-Type non supportato da Gemini
  'dedup_hit',          // già presente nel batch o già indicizzato sul tenant (sha256)
  'too_small',          // dimensione < 50KB (probabile icona/spacer)
  'too_large',          // dimensione > 10MB (limite hard download)
  'fetch_failed',       // download HTTP fallito (timeout / non-200 / network)
  'limit_reached',      // hit limite per-pagina (50 immagini) o per-session (200MB)
  'embed_failed',       // generazione embedding Gemini fallita
  'upload_failed',      // upload R2 fallito
  'insert_failed',      // INSERT chunk DB fallito
  'gate_non_gemini',    // tenant ha embedder ≠ gemini (limitazione tecnica nota)
  'gate_disabled',      // setting wiki_scrape_index_media = false
] as const;

export type MediaSkipReason = typeof MEDIA_SKIP_REASONS[number];

/**
 * Candidato media estratto dal DOM di una pagina web. Vive in memoria
 * tra l'extractor sync e l'ingester async (download + R2 + embed).
 */
export interface ScrapedMedia {
  /** URL assoluto risolto rispetto al baseUrl della pagina. */
  sourceUrl: string;
  /** Attributo `alt` del tag `<img>` (max 500 char). */
  alt: string;
  /** `<figcaption>` più vicino quando presente (max 500 char). */
  caption: string;
  /** Indice 0-based di apparizione nel DOM (preservato per il contesto). */
  positionInPage: number;
  /**
   * MIME hint dedotto dall'extension del path. `null` se sconosciuto: il
   * MIME reale viene riletto dal Content-Type della response.
   */
  mimeHint: string | null;
}

/**
 * Esito aggregato dell'ingest media per uno scrape. Restituito al caller
 * (route KB / worker wiki) e usato per scrivere la riga di telemetria
 * estesa in `scrape_telemetry`.
 */
export interface MediaIngestResult {
  /** Numero candidati estratti dal DOM (post-filtri sync dell'extractor). */
  extractedCount: number;
  /** Numero chunk multimodali effettivamente inseriti nel DB. */
  indexedCount: number;
  /** Conteggio aggregato per ciascun motivo di skip (parser + ingester). */
  skippedReasons: Partial<Record<MediaSkipReason, number>>;
  /** Bytes totali scaricati (somma `Content-Length` reali post-fetch). */
  bytesTotal: number;
  /** True se la pipeline è stata skippata interamente per gating. */
  skipped: boolean;
  /** Reason del gating quando `skipped=true`. */
  skipReason?: MediaSkipReason;
}

export interface ProductSnapshot {
  /** Timestamp risolto (ISO). Se richiesta NOW, è il momento del calcolo. */
  as_of: string;
  /** 'live' = calcolato da pos_stock corrente; 'replay' = reverse-replay storico. */
  derived_from: 'live' | 'replay';
  product: ProductSnapshotProduct;
  total_qty: number;
  total_reserved: number;
  total_available: number;
  /** Null when one or more residual layers lack an immutable cost snapshot. */
  value_cmp: number | null;
  value_sell: number;
  /** Stato lifecycle "dominante" al `as_of` (max-count tra le righe attive). */
  dominant_status: string | null;
  breakdown: SnapshotBreakdownEntry[];
  serials: SnapshotSerialEntry[];
  cost_stats: ProductCostStatsGlobal;
  /** distribuzione stati logistici per quantità (bucket `pos_stock`) per la testata. */
  status_distribution: SnapshotStatusDistEntry[];
  /** ultimo movimento di stato (ISO) ≤ as_of. */
  last_movement_at: string | null;
  cutover_warning: string | null;
  /** riconciliazione ledger vs giacenza materializzata. */
  reconciliation: SnapshotReconciliation;
}

// ----------------------------------------------------------------------------
// Report + correzione di riconciliazione giacenza (pos_stock) vs
// ledger ricostruito (pos_stock_movements). Grain = (product, warehouse, lot,
// variant). SoT = ledger; la correzione riallinea `pos_stock` solo dove sicuro.
// ----------------------------------------------------------------------------

/** Esito della correzione per una singola riga di grain divergente. */
export type StockReconciliationAction =
  | 'increase_in_stock'   // ledger > materiale → bucket `in_stock` incrementato
  | 'decrease_in_stock'   // ledger < materiale → bucket `in_stock` decrementato
  | 'skipped_unsafe';     // decremento non applicabile senza bucket negativo

/** Riga del report/correzione al grain (product, warehouse, lot, variant). */
export interface StockReconciliationRow {
  product_id: string;
  product_sku: string | null;
  product_name: string | null;
  warehouse_id: string | null;
  warehouse_name: string | null;
  lot_id: string | null;
  lot_number: string | null;
  variant_id: string | null;
  /** Giacenza ricostruita dal ledger per il grain. */
  ledger_qty: number;
  /** Giacenza materializzata (Σ bucket `pos_stock`) per il grain. */
  materialized_qty: number;
  /** `ledger_qty − materialized_qty`. */
  discrepancy: number;
  /** Azione (proposta in dry-run, applicata con `apply=true`). */
  action: StockReconciliationAction;
  /** True se la riga è stata effettivamente scritta (solo con `apply=true`). */
  applied: boolean;
}

/** Payload del report/correzione di riconciliazione. */
export interface StockReconciliationReport {
  /** False = dry-run (nessuna scrittura); True = correzione applicata. */
  applied: boolean;
  /** Prodotto singolo se richiesto, altrimenti null (intero tenant). */
  product_id: string | null;
  /** Righe di grain divergenti (predicato identico a quello della correzione). */
  rows: StockReconciliationRow[];
  /** Conteggio righe divergenti. */
  total_divergent: number;
  /** Somma degli scarti assoluti (ampiezza complessiva della divergenza). */
  total_abs_discrepancy: number;
  /** Righe non correggibili in sicurezza (decremento oltre il bucket disponibile). */
  skipped_unsafe: number;
}

/** dettaglio singolo seriale (modale "Vista serializzata", 4 blocchi). */
export interface SerialDetail {
  /** Blocco 1 — anagrafica. */
  serial_id: string;
  /** Identificatore primario dell'unità (ordine macaddr→imei→iccid→generic). `null` se l'unità non ha ancora identificatori associati. */
  serial_number: string | null;
  kind: string | null;
  /** xxx — tutti gli identificatori dell'unità fisica (IMEI×2 + ICCID, ecc.), ordinati macaddr→imei→iccid→generic. */
  identifiers: Array<{ kind: string; value: string }>;
  product_id: string;
  product_name: string;
  /** URL immagine primaria del prodotto (pos_product_images, `is_primary DESC, sort_order`). `null` se il prodotto non ha immagini. Stessa fonte della Vista Cross-Store. */
  image_url: string | null;
  sku: string | null;
  ean: string | null;
  brand_name: string | null;
  category_name: string | null;
  /** tipologia reale (pos_product_types.name) + attributi variante testuali. */
  product_type_name: string | null;
  variant_info: string | null;
  /** attributi REALI della variante collegata al seriale (Memoria/Colore/Condizione). NULL se la unit non ha variante. */
  variant_attributes: PosProductVariantAttributes | null;
  /** Blocco 2 — stato & ubicazione. */
  stock_status: string;
  logistic_status: ProductLogisticStatus | null;
  warehouse_id: string | null;
  warehouse_name: string | null;
  /** Blocco 3 — fornitore & lotto + IVA + DDT. */
  lot_id: string | null;
  lot_number: string | null;
  supplier_sku: string | null;
  supplier_name: string | null;
  unit_cost: number | null;
  carico_date: string | null;
  source_type: string | null;
  source_doc_ref: string | null;
  vat_code: string | null;
  vat_rate: number | null;
  vat_label: string | null;
  vat_nature: string | null;
  /** Blocco 4 — timeline eventi. */
  timeline: SerialTimelineEntry[];
}

export interface SerialTimelineEntry {
  id: string;
  created_at: string;
  type: string;
  warehouse_id: string | null;
  warehouse_name: string | null;
  to_warehouse_id: string | null;
  to_warehouse_name: string | null;
  present_before: number;
  present_after: number;
  reference_type: string | null;
  reference_id: string | null;
  user_name: string | null;
  notes: string | null;
}


// ============================================================================
// Prima Nota: KPI POS-retail + viste detail/daily.
// ============================================================================

export type PrimaNotaView = 'detail' | 'daily';
/**
 * Sorgenti documentali in Prima Nota. Sia la cassa POS sia l'amministrazione
 * possono generare scontrini, ricevute (attestati di vendita), fatture e note.
 * Le sorgenti riflettono il TIPO di documento, non il canale di emissione.
 */
export type PrimaNotaSource =
  | 'scontrino'              // pos_fiscal_documents.document_type='scontrino'/'receipt' (entrata)
  | 'ricevuta'               // pos_fiscal_documents.document_type='ricevuta' (entrata)
  | 'fattura_attiva'         // invoices doc_type='TD01' (entrata)
  | 'nota_credito_attiva'    // invoices doc_type='TD04' (uscita: storno verso cliente)
  | 'nota_debito_attiva'     // invoices doc_type='TD05' (entrata)
  | 'fattura_passiva'        // supplier_invoices con doc_type NOT IN (TD04,TD05,TD08) (uscita)
  | 'nota_credito_passiva'   // supplier_invoices doc_type TD04/TD08 (entrata: rimborso)
  | 'nota_debito_passiva'    // supplier_invoices doc_type TD05 (uscita)
  | 'manuale';               // pos_cash_book con !is_auto_generated
export type PrimaNotaEntryType = 'entrata' | 'uscita';
export type PrimaNotaAccountType = 'cassa_contanti' | 'cassa_carte' | 'banca' | 'crediti_clienti';

export interface PrimaNotaVatBreakdownEntry {
  /** Aliquota IVA in percentuale (es. 4, 5, 10, 22, 0 per esente). */
  rate: number;
  /** Etichetta leggibile (es. "22%", "Esente"). */
  label: string;
  /** Imponibile cumulato in euro. */
  taxable: number;
  /** IVA cumulata in euro. */
  vat: number;
}

export interface PrimaNotaPaymentMixEntry {
  /** Codice metodo (es. `cash`, `card`, `bonifico`). `unknown` se assente. */
  code: string;
  /** Nome leggibile per UI. */
  name: string;
  /** Trattamento RT {0,1,2,3,5}. `null` se metodo sconosciuto/non collegato.
   *  sostituisce `ade_category`. */
  ade_code: 0 | 1 | 2 | 3 | 5 | null;
  /** Numero scontrini con quel metodo. */
  count: number;
  /** Totale incassato con quel metodo. */
  total: number;
  /** Percentuale sul totale incasso (0..100). */
  pct: number;
}

export interface PrimaNotaKpiDelta {
  current: number;
  previous: number;
  /** Δ% in punti percentuali (es. +12.5). `null` se base previous=0. */
  delta_pct: number | null;
}

export interface PrimaNotaSourceCount {
  source: PrimaNotaSource;
  count: number;
  total: number;
}

export interface PrimaNotaKpis {
  /** Totale entrate del periodo (somma su tutte le sorgenti entrata). */
  entrate: PrimaNotaKpiDelta;
  /** Totale uscite del periodo (somma su tutte le sorgenti uscita). */
  uscite: PrimaNotaKpiDelta;
  /** Saldo netto = entrate.current − uscite.current. */
  saldo_netto: PrimaNotaKpiDelta;
  /** Numero totale movimenti del periodo. */
  movimenti_count: PrimaNotaKpiDelta;
  /** Breakdown del numero di movimenti per sorgente (per il mini-grafico). */
  count_by_source: PrimaNotaSourceCount[];
  /** Mix metodi pagamento (scontrini + ricevute). */
  payment_mix: PrimaNotaPaymentMixEntry[];
  /** Split imponibile/IVA per aliquota (entrate fiscali). */
  vat_breakdown: PrimaNotaVatBreakdownEntry[];
}

export interface PrimaNotaDetailItem {
  id: string;
  source: PrimaNotaSource;
  entry_date: string;
  entry_type: PrimaNotaEntryType;
  account_type: string;
  description: string;
  amount: number;
  /** Totale incluso IVA per scontrini/fatture; uguale a amount per manuali. */
  total: number;
  reference_type: string | null;
  reference_id: string | null;
  /** Numero documento (sale_number / invoice numero / null per manuali). */
  receipt_number: string | null;
  store_id: string | null;
  store_name: string | null;
  customer_name: string | null;
  payment_method_code: string | null;
  payment_method_name: string | null;
  /** Imponibile (per scontrino/fattura). */
  taxable: number | null;
  /** IVA totale (per scontrino/fattura). */
  vat_total: number | null;
  /** Split per aliquota (vuoto per manuali / fatture senza dettaglio). */
  vat_breakdown: PrimaNotaVatBreakdownEntry[];
  notes: string | null;
  status: string | null;
}

export interface PrimaNotaDailyItem {
  /** ISO date (YYYY-MM-DD). */
  day: string;
  /** Totale entrate del giorno (tutte le sorgenti entrata). */
  total_in: number;
  /** Totale uscite del giorno (tutte le sorgenti uscita). */
  total_out: number;
  /** Netto = total_in - total_out. */
  total_net: number;
  /** Mix metodi pagamento del giorno (entrate fiscali con metodo). */
  payment_mix: PrimaNotaPaymentMixEntry[];
  /** Split IVA del giorno (aggregato su tutte le sorgenti fiscali). */
  vat_breakdown: PrimaNotaVatBreakdownEntry[];
  /** Numero totale movimenti del giorno (tutte le sorgenti). */
  entries_count: number;
  /** Breakdown del numero di movimenti per sorgente. */
  count_by_source: PrimaNotaSourceCount[];
}

/**
 * Filtri input boundary HTTP per `GET /pos/prima-nota`.
 * Tutti i campi opzionali; il backend applica i default.
 */
export interface PrimaNotaFiltersInput {
  from_date?: string;
  to_date?: string;
  account?: PrimaNotaAccountType;
  entry_type?: PrimaNotaEntryType;
  source?: PrimaNotaSource;
  store_id?: string;
  payment_method?: string;
  /** Aliquota IVA in percentuale (filtra movimenti fiscali con quella aliquota). */
  vat_rate?: number;
  view?: PrimaNotaView;
  limit?: number;
  offset?: number;
}

/**
 * Input boundary HTTP per `POST /pos/prima-nota/manual`.
 */
export interface PrimaNotaManualEntryInput {
  entry_type: PrimaNotaEntryType;
  account_type: PrimaNotaAccountType;
  description: string;
  amount: number;
  entry_date?: string;
  notes?: string;
}

export interface PrimaNotaResponse {
  view: PrimaNotaView;
  from_date: string;
  to_date: string;
  /** Detail: lista movimenti enriched. Daily: 1 riga per giorno. */
  items: PrimaNotaDetailItem[] | PrimaNotaDailyItem[];
  kpi: PrimaNotaKpis;
  /** Paginazione (solo `view=detail`): record totali nel periodo prima del LIMIT. */
  total: number;
  /** Limit applicato (default 100, max 500). Sempre presente per riflettere la richiesta. */
  limit: number;
  /** Offset applicato (default 0). */
  offset: number;
}

// ============================================================================
// Documenti Fiscali (CashBook unificato Attive + Passive)
// ============================================================================

/**
 * Direzione di un documento fiscale rispetto al tenant.
 * - `attiva`  → emesso (lato vendita), persisted in `invoices`
 * - `passiva` → ricevuto (lato fornitore), persisted in `supplier_invoices`
 */
export type FiscalDocumentDirection = 'attiva' | 'passiva';

/** Categoria documentale unificata (incluse ricevute non-SDI). */
export type FiscalDocumentCategory =
  | 'fattura'
  | 'nota_credito'
  | 'nota_debito'
  | 'autofattura'
  | 'parcella'
  | 'ricevuta'
  | 'credito_patrimoniale';

/** Tutti i collegamenti verificati, senza riduzione al primo documento. */
export const FiscalDocumentInlineLinkSchema = z.object({
  kind: z.enum(['sale', 'ddt', 'purchase_order']),
  id: z.string().uuid(),
  label: z.string(),
  basis: z.enum(['source', 'structured_reference', 'manual']),
  quantity_invoiced: z.number().finite().nonnegative().nullable(),
});
export type FiscalDocumentInlineLink = z.infer<typeof FiscalDocumentInlineLinkSchema>;

/** Riga ritornata da `GET /api/fiscal-documents`. */
export const FiscalDocumentRowSchema = z.object({
  id: z.string().uuid(),
  direction: z.enum(['attiva', 'passiva']),
  document_category: z.enum([
    'fattura', 'nota_credito', 'nota_debito', 'autofattura',
    'parcella', 'ricevuta', 'credito_patrimoniale',
  ]),
  doc_type: z.string().nullable(),
  numero: z.string().nullable(),
  data_documento: z.string().nullable(),
  data_scadenza: z.string().nullable(),
  controparte: z.string().nullable(),
  controparte_vat: z.string().nullable(),
  imponibile: z.number().finite(),
  iva: z.number().finite(),
  totale: z.number().finite(),
  status: z.string().nullable(),
  identity_status: supplierInvoiceIdentityStatusSchema.nullable(),
  sezionale_id: z.string().nullable(),
  regime_iva: z.string().nullable(),
  has_xml: z.boolean(),
  has_pdf: z.boolean(),
  has_ddt: z.boolean(),
  rs_name: z.string().nullable(),
  customer_vat: z.string().nullable(),
  conservation_status: z.enum(['preserved', 'pending', 'error']).nullable(),
  metadata: z.record(z.unknown()).nullable(),
  inline_links: z.array(FiscalDocumentInlineLinkSchema),
  created_at: z.string().nullable(),
});
export type FiscalDocumentRow = z.infer<typeof FiscalDocumentRowSchema>;

/** Response paginata di `GET /api/fiscal-documents`. */
export const FiscalDocumentListResponseSchema = z.object({
  items: z.array(FiscalDocumentRowSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});
export type FiscalDocumentListResponse = z.infer<typeof FiscalDocumentListResponseSchema>;

// ============================================================================
// Zod schemas — validazione boundary HTTP per `/pos/prima-nota` (#1459)
// ============================================================================

export const PrimaNotaVatBreakdownEntrySchema = z.object({
  rate: z.number(),
  label: z.string(),
  taxable: z.number(),
  vat: z.number(),
});

export const PrimaNotaPaymentMixEntrySchema = z.object({
  code: z.string(),
  name: z.string(),
  ade_code: z.union([
    z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(5),
    z.null(),
  ]),
  count: z.number(),
  total: z.number(),
  pct: z.number(),
});

export const PrimaNotaKpiDeltaSchema = z.object({
  current: z.number(),
  previous: z.number(),
  delta_pct: z.number().nullable(),
});

export const PrimaNotaSourceSchema = z.enum([
  'scontrino', 'ricevuta',
  'fattura_attiva', 'nota_credito_attiva', 'nota_debito_attiva',
  'fattura_passiva', 'nota_credito_passiva', 'nota_debito_passiva',
  'manuale',
]);

export const PrimaNotaAccountTypeSchema = z.enum([
  'cassa_contanti', 'cassa_carte', 'banca', 'crediti_clienti',
]);

export const PrimaNotaEntryTypeSchema = z.enum(['entrata', 'uscita']);

export const PrimaNotaSourceCountSchema = z.object({
  source: PrimaNotaSourceSchema,
  count: z.number(),
  total: z.number(),
});

export const PrimaNotaKpisSchema = z.object({
  entrate: PrimaNotaKpiDeltaSchema,
  uscite: PrimaNotaKpiDeltaSchema,
  saldo_netto: PrimaNotaKpiDeltaSchema,
  movimenti_count: PrimaNotaKpiDeltaSchema,
  count_by_source: z.array(PrimaNotaSourceCountSchema),
  payment_mix: z.array(PrimaNotaPaymentMixEntrySchema),
  vat_breakdown: z.array(PrimaNotaVatBreakdownEntrySchema),
});

export const PrimaNotaDetailItemSchema = z.object({
  id: z.string(),
  source: PrimaNotaSourceSchema,
  entry_date: z.string(),
  entry_type: PrimaNotaEntryTypeSchema,
  account_type: z.string(),
  description: z.string(),
  amount: z.number(),
  total: z.number(),
  reference_type: z.string().nullable(),
  reference_id: z.string().nullable(),
  receipt_number: z.string().nullable(),
  store_id: z.string().nullable(),
  store_name: z.string().nullable(),
  customer_name: z.string().nullable(),
  payment_method_code: z.string().nullable(),
  payment_method_name: z.string().nullable(),
  taxable: z.number().nullable(),
  vat_total: z.number().nullable(),
  vat_breakdown: z.array(PrimaNotaVatBreakdownEntrySchema),
  notes: z.string().nullable(),
  status: z.string().nullable(),
});

export const PrimaNotaDailyItemSchema = z.object({
  day: z.string(),
  total_in: z.number(),
  total_out: z.number(),
  total_net: z.number(),
  payment_mix: z.array(PrimaNotaPaymentMixEntrySchema),
  vat_breakdown: z.array(PrimaNotaVatBreakdownEntrySchema),
  entries_count: z.number(),
  count_by_source: z.array(PrimaNotaSourceCountSchema),
});

/** Coerce stringhe da query string. */
const numFromString = z.preprocess(
  (v) => (v === '' || v === undefined || v === null ? undefined : Number(v)),
  z.number().optional(),
);

export const PrimaNotaFiltersInputSchema = z.object({
  from_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  account: PrimaNotaAccountTypeSchema.optional(),
  entry_type: PrimaNotaEntryTypeSchema.optional(),
  source: PrimaNotaSourceSchema.optional(),
  store_id: z.string().uuid().optional(),
  payment_method: z.string().min(1).max(64).optional(),
  vat_rate: numFromString,
  view: z.enum(['detail', 'daily']).optional(),
  limit: numFromString,
  offset: numFromString,
}).strip();

export const PrimaNotaManualEntryInputSchema = z.object({
  entry_type: PrimaNotaEntryTypeSchema,
  account_type: PrimaNotaAccountTypeSchema,
  description: z.string().min(1).max(500),
  amount: z.number().positive(),
  entry_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  notes: z.string().max(1000).optional(),
});

export const PrimaNotaResponseSchema = z.object({
  view: z.enum(['detail', 'daily']),
  from_date: z.string(),
  to_date: z.string(),
  items: z.union([
    z.array(PrimaNotaDetailItemSchema),
    z.array(PrimaNotaDailyItemSchema),
  ]),
  kpi: PrimaNotaKpisSchema,
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});

// Risposta dell'endpoint Workspace STT
// `POST /api/personal-assistant/workspace/stt`
export interface WorkspaceSttResponse {
  /** Trascrizione testuale (trim, italiano default). */
  text: string;
  /** Durata audio originale in millisecondi (0 se non fornita dal client). */
  durationMs: number;
}


// =============================================================================
// Master AI Routing aggregati per pagina /master/ai-models.
// =============================================================================

export interface AIRoutingServiceItem {
  service_key: string;
  display_name: string;
  category: string;
  capabilities: Record<string, boolean>;
  default: { provider: string; model: string };
  overrides: { count: number; byProvider: Record<string, number> };
  usage: { requests_24h: number; requests_7d: number; active_tenants: number };
}

export interface AIRoutingServicesResponse {
  items: AIRoutingServiceItem[];
  generated_at: string;
}

export interface AIRoutingOverrideEntry {
  tenant_id: string;
  tenant_name: string;
  provider: string;
  model: string | null;
  source: 'tenant_service_routing' | 'tenant_settings';
  updated_at: string;
  updated_by_email: string | null;
}

export interface AIRoutingOverridesResponse {
  service_key: string;
  entries: AIRoutingOverrideEntry[];
}

// =============================================================================
// Catalog seriali tipizzati (IMEI/ICCID/MAC/serial) + unit + specs.
// =============================================================================

export type SerialKind = 'imei' | 'iccid' | 'macaddr' | 'serial';

/**
 * Forma di `pos_products.serial_kinds_required`.
 * Esempio dual-IMEI smartphone: [{kind:'imei',count:2},{kind:'iccid',count:1}].
 * Solo per prodotti con `serial_mode='unique'`.
 */
export interface SerialKindRequirement {
  kind: SerialKind;
  count: number;
}

/**
 * Forma di `pos_products.specs`. Attributi tecnici liberi key/value
 * (memoria, colore, capacità, ecc.). Tipizzato come record di string|number|boolean
 * per evitare `any` e mantenere serializzabile in JSONB.
 */
export type ProductSpecs = Record<string, string | number | boolean | null>;

export interface ProductSerialEntry {
  id: string;
  kind: SerialKind;
  serial_number: string;
  value_normalized: string | null;
  status: string;
}

export interface ProductSerialUnit {
  id: string;
  product_id: string;
  warehouse_id: string | null;
  lot_id: string | null;
  status: string;
  unit_cost: number | null;
  carico_date: string;
  created_at: string;
  serials: ProductSerialEntry[];
}

export const SerialKindRequirementSchema = z.object({
  kind: z.enum(['imei','iccid','macaddr','serial']),
  count: z.number().int().min(1).max(10),
});
export const SerialKindsRequiredSchema = z.array(SerialKindRequirementSchema);
export const ProductSpecsSchema = z.record(z.union([z.string(), z.number(), z.boolean(), z.null()]));

// ============================================================================
// Per-tenant Knowledge Base metadata schema
// ============================================================================

export const KB_METADATA_DIMENSIONS = ['language', 'audience', 'sensitivity'] as const;
export type KbMetadataDimension = (typeof KB_METADATA_DIMENSIONS)[number];

export const KbMetadataValueSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9_-]+$/, 'Solo lowercase, cifre, _ e -');

export const KbMetadataLabelSchema = z.string().min(1).max(120);

export const KbMetadataOptionSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  dimension: z.enum(KB_METADATA_DIMENSIONS),
  value: KbMetadataValueSchema,
  label: KbMetadataLabelSchema,
  position: z.number().int().min(0).max(10000),
  is_default: z.boolean(),
  is_system: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type KbMetadataOption = z.infer<typeof KbMetadataOptionSchema>;

export const KbMetadataOptionCreateSchema = z.object({
  dimension: z.enum(KB_METADATA_DIMENSIONS),
  value: KbMetadataValueSchema,
  label: KbMetadataLabelSchema,
  position: z.number().int().min(0).max(10000).optional(),
  is_default: z.boolean().optional(),
});
export type KbMetadataOptionCreate = z.infer<typeof KbMetadataOptionCreateSchema>;

export const KbMetadataOptionUpdateSchema = z.object({
  label: KbMetadataLabelSchema.optional(),
  position: z.number().int().min(0).max(10000).optional(),
  is_default: z.boolean().optional(),
});
export type KbMetadataOptionUpdate = z.infer<typeof KbMetadataOptionUpdateSchema>;

/**
 * Metadata applicata a un documento/URL KB durante l'ingest.
 * - language: single-value (riusa colonna esistente ai_agent_documents.language)
 * - audience: multi-value (text[])
 * - sensitivity: single-value
 */
export const KbDocumentMetadataSchema = z.object({
  language: KbMetadataValueSchema.optional().nullable(),
  audience: z.array(KbMetadataValueSchema).max(16).optional(),
  sensitivity: KbMetadataValueSchema.optional().nullable(),
});
export type KbDocumentMetadata = z.infer<typeof KbDocumentMetadataSchema>;

/**
 * Filtri retrieval RAG passati dal client (chat UI) per
 * restringere i chunk visibili dell'agente alle audience autorizzate e
 * al livello di sensibilità dell'utente che sta chattando.
 *   - audience: array opzionale di valori del catalog tenant. Filtra per
 *     overlap con `ai_agent_chunks.audience` (i chunk con audience vuota
 *     restano sempre visibili, sono "tutti").
 *   - sensitivity: singolo valore opzionale. Filtra per uguaglianza su
 *     `ai_agent_chunks.sensitivity` (i chunk con sensitivity NULL
 *     restano sempre visibili, sono "non classificati").
 * Quando assente o vuoto = nessuna restrizione.
 */
export const RagRetrievalFiltersSchema = z.object({
  audience: z.array(KbMetadataValueSchema).max(16).optional(),
  sensitivity: KbMetadataValueSchema.optional().nullable(),
});
export type RagRetrievalFilters = z.infer<typeof RagRetrievalFiltersSchema>;

/**
 * RAG Analytics: documenti tenant-wide cross-agent.
 * Mount: protectedRouter.use('/rag-analytics', ...) — tenant da JWT, admin-only sui write.
 */
export const RagAdminDocumentSchema = z.object({
  id: z.string().uuid(),
  agent_id: z.string().uuid(),
  agent_name: z.string(),
  agent_type: z.string().nullable(),
  filename: z.string(),
  file_type: z.string(),
  file_size: z.number().int().nonnegative(),
  status: z.string(),
  embedding_status: z.string().nullable(),
  ocr_status: z.string().nullable(),
  error_message: z.string().nullable(),
  language: z.string().nullable(),
  audience: z.array(z.string()).nullable(),
  sensitivity: z.string().nullable(),
  source_r2_key: z.string().nullable(),
  chunk_count: z.number().int().nonnegative(),
  total_tokens: z.number().int().nonnegative(),
  created_by: z.string().uuid().nullable(),
  created_by_email: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type RagAdminDocument = z.infer<typeof RagAdminDocumentSchema>;

export const RagAdminDocumentsAggregatesSchema = z.object({
  totalDocs: z.number().int().nonnegative(),
  totalChunks: z.number().int().nonnegative(),
  totalTokens: z.number().int().nonnegative(),
  totalBytes: z.number().int().nonnegative(),
});
export type RagAdminDocumentsAggregates = z.infer<typeof RagAdminDocumentsAggregatesSchema>;

export const RagAdminDocumentsListResponseSchema = z.object({
  items: z.array(RagAdminDocumentSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().nonnegative(),
  aggregates: RagAdminDocumentsAggregatesSchema,
});
export type RagAdminDocumentsListResponse = z.infer<typeof RagAdminDocumentsListResponseSchema>;

export const RagAdminBulkDeleteSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(500),
});
export type RagAdminBulkDelete = z.infer<typeof RagAdminBulkDeleteSchema>;

export const RagAdminBulkDeleteResultSchema = z.object({
  deleted: z.number().int().nonnegative(),
  results: z.array(z.object({
    id: z.string().uuid(),
    ok: z.boolean(),
    error: z.string().optional(),
  })),
});
export type RagAdminBulkDeleteResult = z.infer<typeof RagAdminBulkDeleteResultSchema>;

export const RagAdminBulkMetadataSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(500),
  language: KbMetadataValueSchema.optional().nullable(),
  audience: z.array(KbMetadataValueSchema).max(16).optional(),
  sensitivity: KbMetadataValueSchema.optional().nullable(),
});
export type RagAdminBulkMetadata = z.infer<typeof RagAdminBulkMetadataSchema>;

export const RagAdminReingestResultSchema = z.object({
  id: z.string().uuid(),
  jobId: z.string().nullable(),
  enqueued: z.boolean(),
});
export type RagAdminReingestResult = z.infer<typeof RagAdminReingestResultSchema>;

// ============================================================================
// Storage Quota: tipi condivisi BE/FE per snapshot, errori 422
// e payload notifiche server-side categoria `storage_quota`.
// ============================================================================

export type QuotaThreshold = 85 | 95 | 100 | 105;

export type StorageQuotaErrorCode = 'STORAGE_QUOTA_EXCEEDED' | 'DB_QUOTA_EXCEEDED_EMBEDDINGS';

export interface StorageQuotaErrorPayload {
  errorCode: StorageQuotaErrorCode;
  scope: QuotaScope;
  blockedCategory: QuotaCategory;
  message: string;
  endpoint: string;
  pct: number | null;
  emergencyUntil: string | null;
  ts: number;
}

export interface StorageQuotaNotificationMetadata {
  scope: QuotaScope;
  threshold: QuotaThreshold | null;
  pct: number;
  usedBytes: number;
  maxBytes: number;
  blockedCategories: QuotaCategory[];
  emergencyUntil: string | null;
  kind: 'threshold' | 'recovery';
  [key: string]: unknown;
}

// ============================================================================
// Geo-blocking SIP enterprise (MaxMind + nftables + Lua per-tenant)
// ============================================================================

declare const __countryCodeBrand: unique symbol;
/**
 * Country ISO 3166-1 alpha-2 (es. "IT", "US", "RU"). Branded type per evitare
 * accidentale mescolamento con stringhe generiche nei contratti API e nei
 * service. Costruire SOLO via `countryCodeOrNull()` o `CountryCodeSchema.parse`.
 */
export type CountryCode = string & { readonly [__countryCodeBrand]: 'CountryCode' };

export const CountryCodeSchema = z
  .string()
  .regex(/^[A-Z]{2}$/, 'CountryCode deve essere ISO 3166-1 alpha-2 (2 lettere maiuscole)')
  .transform((v) => v as CountryCode);

export function countryCodeOrNull(value: string | null | undefined): CountryCode | null {
  if (!value) return null;
  const upper = value.toUpperCase();
  return /^[A-Z]{2}$/.test(upper) ? (upper as CountryCode) : null;
}

export const GEO_POLICY_MODES = ['disabled', 'whitelist', 'blacklist'] as const;
export type GeoPolicyMode = typeof GEO_POLICY_MODES[number];
export const GeoPolicyModeSchema = z.enum(GEO_POLICY_MODES);

export const NFT_SET_MODES = ['log_only', 'enforce'] as const;
export type NftSetMode = typeof NFT_SET_MODES[number];
export const NftSetModeSchema = z.enum(NFT_SET_MODES);

export interface TenantGeoPolicy {
  tenant_id: string;
  mode: GeoPolicyMode;
  allowed_countries: CountryCode[];
  enabled: boolean;
  updated_at: string | null;
}

export const TenantGeoPolicyUpdateSchema = z.object({
  mode: GeoPolicyModeSchema,
  allowed_countries: z.array(CountryCodeSchema).max(250),
});
export type TenantGeoPolicyUpdate = z.infer<typeof TenantGeoPolicyUpdateSchema>;

export interface MaxMindStatus {
  available: boolean;
  db_path: string | null;
  bytes: number | null;
  build_epoch: number | null;
  last_sync_at: string | null;
  last_sync_status: 'success' | 'failed' | 'running' | null;
  last_error: string | null;
  next_scheduled_sync_at: string | null;
}

export interface NftablesSetStatus {
  set_name: string;
  mode: NftSetMode;
  countries: CountryCode[];
  cidr_count: number;
  last_refresh_at: string | null;
  last_refresh_ok: boolean | null;
  last_error: string | null;
}

export interface KernelDropMetricsBucket {
  observed_at: string;
  packets: number;
  bytes: number;
  node: string;
}

export interface KernelDropMetrics {
  set_name: string;
  total_packets_24h: number;
  total_bytes_24h: number;
  buckets: KernelDropMetricsBucket[];
  top_countries: { country_code: CountryCode | null; packets: number }[];
  top_ips: { source_ip: string; packets: number }[];
}

export interface LuaRejectMetrics {
  total_24h: number;
  by_tenant: { tenant_id: string; tenant_name: string | null; count: number }[];
  by_country: { country_code: CountryCode | null; count: number }[];
}

export interface GeoBlockedRegisterEvent {
  id: string;
  tenant_id: string | null;
  tenant_name: string | null;
  source_ip: string;
  country_code: CountryCode | null;
  policy_mode: GeoPolicyMode;
  reason: string;
  sip_method: string | null;
  user_agent: string | null;
  blocked_at: string;
}


// ===== S3 — Redis health snapshot =====

export type RedisRole = 'master' | 'slave' | 'replica' | 'unknown';

/** status semaforo per metriche AOF / frammentazione memoria. */
export type RedisStatus = 'green' | 'yellow' | 'red';

export interface RedisHealthSnapshot {
  ok: true;
  endpoint: string;
  db: number;
  latency_ms: number;
  server: {
    version: string;
    uptime_seconds: number;
    role: RedisRole;
  };
  clients: {
    connected: number;
    blocked: number;
    tracking: number;
    max: number;
  };
  memory: {
    used_bytes: number;
    used_human: string;
    maxmemory_bytes: number;
    maxmemory_human: string;
    rss_bytes: number;
    fragmentation_ratio: number;
    /** status green/yellow/red. Rilevante solo se used_bytes supera la soglia configurata in `infra-monitor.service.ts` (default 100MB). */
    fragmentation_status: RedisStatus;
    /** quota memoria used/maxmemory in %. 0 se maxmemory=0 (illimitato). */
    used_pct: number;
    /** semaforo quota memoria (yellow >=70%, red >=85%). Green se maxmemory=0. */
    used_pct_status: RedisStatus;
  };
  persistence: {
    aof_enabled: boolean;
    aof_last_status: string;
    aof_last_rewrite_status: string;
    rdb_last_save_time: number;
    rdb_last_bgsave_status: string;
    rdb_changes_since_last_save: number;
    /** telemetria AOF estesa (Redis INFO persistence). */
    aof_size_bytes: number;
    aof_size_human: string;
    aof_base_bytes: number;
    /** size/base — soglie configurabili in `infra-monitor.service.ts` (default yellow >50, red >100). */
    aof_growth_ratio: number;
    aof_growth_status: RedisStatus;
    /** Età in secondi dall'ultimo rewrite osservato (null = nessun rewrite mai osservato dal monitor). Tracciato persistentemente in Redis (`infra:redis:aof_last_rewrite_at`). */
    aof_last_rewrite_age_sec: number | null;
    aof_last_rewrite_age_status: RedisStatus;
  };
  replication: {
    role: RedisRole;
    connected_slaves: number;
    master_link_status: string | null;
    master_repl_offset: number;
    slave_repl_offset: number;
  };
  ops: {
    instantaneous_ops_per_sec: number;
    total_commands_processed: number;
    total_connections_received: number;
    keyspace_hits: number;
    keyspace_misses: number;
    rejected_connections: number;
  };
  /** Backup RDB e snapshot ACL su REDIS-1 (master_cron_job_logs). */
  ops_backup: {
    rdb: RedisOpSnapshot;
    acl: RedisOpSnapshot;
  };
  /** Mappa role → db index applicato dal factory `getRedis()`. */
  role_db_map: Record<string, number>;
}

/** Stato di un job operativo (backup RDB, snapshot ACL). */
export interface RedisOpSnapshot {
  age_hours: number | null;
  last_status: 'success' | 'failed' | 'unknown';
  status: RedisStatus;
  last_run_at: string | null;
  metadata: Record<string, unknown> | null;
  /**
   * Esito dello smoke-restore (solo per `ops_backup.rdb`). Popolato dal log
   * separato `redis_backup_smoke` su REDIS-1: `ok` se il dump è ricaricabile
   * e contiene almeno una key totale, `failed` se startup o per-DB DBSIZE
   * fallisce, `unknown` per `ops_backup.acl` (smoke non applicabile) o se
   * il job smoke non ha mai girato ancora.
   */
  smoke_status?: 'ok' | 'failed' | 'unknown';
}

export interface RedisHealthError {
  ok: false;
  error: string;
  endpoint?: string;
  db?: number;
  latency_ms: number;
}

export type RedisHealthResponse = RedisHealthSnapshot | RedisHealthError;

// ============================================================================
// Fiscal Agent
// ============================================================================

export interface FiscalAgentActivateResponseData {
  agentId: string;
  name: string;
  token: string;
  /**
   * Canonical server URL the agent must use for socket.io + REST.
   * Source of truth: backend env FISCAL_AGENT_PUBLIC_URL (es. https://fiscal.easyflowai.it).
   * Fallback: backend req.host (solo dev/local; in produzione l'env DEVE essere settato).
   */
  serverUrl?: string;
}

// ─── Workgroups v2 ────────────────────────────────────────
export type WorkgroupCategory = 'project' | 'team' | 'client' | 'initiative';

export interface Workgroup {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  description_md: string | null;
  category: WorkgroupCategory | null;
  avatar_url: string | null;
  cover_image_url: string | null;
  theme_color: string | null;
  badge_icon: string | null;
  owner_id: string | null;
  created_by: string | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  // derivati lato service
  member_count?: number;
  conversation_id?: string | null;
  has_board?: boolean;
  my_role?: 'admin' | 'member';
  board_id?: string;
  board_error?: string;
}

export interface WorkgroupMemberDirectoryEntry {
  user_id: string;
  role: 'admin' | 'member';
  joined_at: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  extension: string | null;
  phone: string | null;
  hr_department_name?: string | null;
  hr_position?: string | null;
  hr_skills?: Array<{ id: string; name: string; level?: string | null }>;
  presence?: 'online' | 'away' | 'offline';
}

export interface WorkgroupHuddle {
  id: string;
  tenant_id: string;
  workgroup_id: string;
  video_room_id: string;
  started_by: string | null;
  started_by_name?: string | null;
  started_at: string;
  ended_at: string | null;
  participant_count: number;
  // derivati
  meeting_link?: string | null;
  duration_seconds?: number | null;
  // recording
  recording_requested?: boolean;
  recording_status?: 'processing' | 'completed' | 'failed' | null;
  recording_available?: boolean;
}

export interface WorkgroupHuddleStartRequest {
  title?: string;
  record?: boolean;
}

export interface WorkgroupHuddleStartResponse {
  huddle: WorkgroupHuddle;
  room: {
    id: string;
    unique_name: string;
    title: string;
    meeting_link: string | null;
  };
}

// Tab Tasks workgroup: shape allineato a quello consumato dal FE.
export interface WorkgroupBoardCount {
  board_id: string | null;
  total: number;
  completed: number;
  in_progress: number;
  cancelled: number;
}

export interface WorkgroupLinkableBoard {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  board_type: 'personal' | 'team' | 'project';
  task_count: number;
  created_at: string;
}

export interface WorkgroupHuddleRecordingUrlResponse {
  status: 'processing' | 'completed' | 'failed' | 'unavailable';
  url: string | null;
}

export interface FiscalAgentConnectionDiagnostic {
  hostname: string;
  resolvedIp?: string | null;
  dnsError?: string | null;
  category: 'ok' | 'auth' | 'namespace' | 'tls' | 'dns' | 'refused' | 'timeout' | 'transport' | 'http_4xx' | 'http_5xx' | 'unknown';
  statusCode?: number | null;
  hint?: string;
  consecutiveFailures: number;
  nextRetryInSec?: number | null;
  lastErrorMessage?: string | null;
  timestamp: string;
}

// ============================================================================
// Feed v2 engagement (bookmark, taxonomic reactions, boost,
// Q&A, RSVP, milestone, analytics).
// ============================================================================

export const FEED_REACTION_KINDS = [
  'like', 'love', 'celebrate', 'idea', 'curious', 'thanks', 'applause', 'custom',
] as const;
export type FeedReactionKind = typeof FEED_REACTION_KINDS[number];

export const FEED_REACTION_KIND_EMOJI: Record<Exclude<FeedReactionKind, 'custom'>, string> = {
  like: '👍',
  love: '❤️',
  celebrate: '🎉',
  idea: '💡',
  curious: '🤔',
  thanks: '🙏',
  applause: '👏',
};

export interface FeedReactionSummary {
  kind: FeedReactionKind;
  emoji: string;
  count: number;
  reacted_by_me?: boolean;
}

export const FEED_POST_TYPES = [
  'message', 'announcement', 'event', 'poll', 'file', 'appreciation', 'question', 'milestone',
] as const;
export type FeedPostType = typeof FEED_POST_TYPES[number];

export interface FeedBookmark {
  id: string;
  tenant_id: string;
  user_id: string;
  post_id: string;
  created_at: string;
}

export const FEED_RSVP_STATUSES = ['yes', 'maybe', 'no'] as const;
export type FeedRsvpStatus = typeof FEED_RSVP_STATUSES[number];

export interface FeedEventRsvp {
  id: string;
  tenant_id: string;
  post_id: string;
  user_id: string;
  status: FeedRsvpStatus;
  updated_at: string;
}

export interface FeedRsvpSummary {
  post_id: string;
  yes: number;
  maybe: number;
  no: number;
  my_status: FeedRsvpStatus | null;
}

export interface FeedBoostRequest {
  hours: number;
}

export interface FeedQuestionAnswer {
  id: string;
  tenant_id: string;
  post_id: string;
  comment_id: string;
  marked_official_by: string;
  marked_at: string;
}

export type FeedMilestoneSourceKind =
  | 'hr_anniversary'
  | 'hr_birthday'
  | 'hr_promotion'
  | 'crm_deal_won'
  | 'pos_revenue_milestone'
  | 'manual';

export interface FeedMilestoneSource {
  kind: FeedMilestoneSourceKind;
  ref_id?: string | null;
  metadata?: Record<string, string | number | boolean | null>;
  icon?: string;
  accent?: string;
}

export interface FeedPostStatsBucket {
  day: string;
  views: number;
  reactions: number;
  comments: number;
  bookmarks: number;
}

export interface FeedPostStats {
  post_id: string;
  title: string | null;
  post_type: FeedPostType;
  created_at: string;
  total_views: number;
  total_reactions: number;
  total_comments: number;
  total_bookmarks: number;
  engagement_rate: number;
  reaction_breakdown: Array<{ kind: FeedReactionKind; count: number }>;
  daily: FeedPostStatsBucket[];
}

export interface FeedAuthorAnalytics {
  range_days: number;
  totals: {
    posts: number;
    reactions: number;
    comments: number;
    bookmarks: number;
    views: number;
    engagement_rate: number;
  };
  top_posts: FeedPostStats[];
}

export interface FeedTabCounts {
  all: number;
  following: number;
  announcements: number;
  mine: number;
  bookmarks: number;
}

// ─── Cross-modulo bridge ──────────────────────────────────
// Tipi condivisi per share universale (CRM/ToDo/Calendar/FileManager → chat/feed),
// entity mentions (@lead/<id>, @deal/<id>, @contact/<id>, @task/<id>, @event/<id>)
// e milestone auto-generate. NO `any` (regola architetturale).

export type CrossEntityKind =
  | 'lead'
  | 'deal'
  | 'contact'
  | 'task'
  | 'event'
  | 'file';

export interface CrossEntityPreview {
  kind: CrossEntityKind;
  id: string;
  title: string;
  subtitle: string | null;
  url: string;
  icon: string;
  accent: string;
  metadata: Record<string, string | number | boolean | null>;
}

export type CrossShareDestinationKind =
  | 'chat_conversation'
  | 'feed_post'
  | 'workgroup_feed';

export interface CrossShareDestination {
  kind: CrossShareDestinationKind;
  target_id: string;
}

export interface CrossShareRequest {
  entity: { kind: CrossEntityKind; id: string };
  destination: CrossShareDestination;
  comment?: string;
}

export interface CrossShareResult {
  ok: true;
  destination: CrossShareDestination;
  message_id?: string;
  post_id?: string;
}

export interface CrossEntityMention {
  kind: CrossEntityKind;
  id: string;
}

export interface CrossMentionSearchItem extends CrossEntityPreview {
  match_score: number;
}

export type FeedMilestoneKind = 'crm_deal_won' | 'hr_hire' | 'hr_birthday';

// ─── Feed v2 entità (topic, post report, editor, audit) ─────
// Tipi derivati da zod schema (single source of truth) in
// `packages/shared/src/schemas/feed.ts`. Import canonico per BE/FE.
export type {
  FeedTopic,
  FeedTopicListItem,
  FeedTopicListResponse,
  FeedPostReport,
  FeedPostReportListItem,
  FeedPostReportListResponse,
  FeedPostReportReason,
  FeedPostReportStatus,
  FeedPostReportResolutionAction,
  FeedEditor,
  FeedEditorListItem,
  FeedEditorListResponse,
  FeedAuditEntry,
  FeedAuditEntryListItem,
  FeedAuditListResponse,
} from '../schemas/feed';

export interface FeedMilestoneSettingRow {
  tenant_id: string;
  kind: FeedMilestoneKind;
  enabled: boolean;
  target_type: 'global' | 'workgroup' | 'department';
  target_value: string | null;
  last_run_at: string | null;
  updated_at: string;
}

// ============================================================================
// SIP Cluster NOC — Zod contracts (a)
// Foundation per endpoint read pianificati in #1868b. Tipi consumati dal
// frontend-master `pages/SipClusterMonitor.tsx` + tabs.
// ============================================================================

export const FsNodeStateSchema = z.enum(['active', 'draining', 'drained', 'down', 'unknown']);
export type FsNodeState = z.infer<typeof FsNodeStateSchema>;

export const FsNodeSchema = z.object({
  id: z.string().min(1),
  publicIp: z.string().nullable(),
  lanIp: z.string().nullable(),
  role: z.enum(['ap', 'standby', 'spare']),
  state: FsNodeStateSchema,
  weight: z.number().int().min(0).max(100),
  setid: z.number().int().min(1),
  channelsActive: z.number().int().min(0),
  channelsLimit: z.number().int().min(0),
  cpuPct: z.number().min(0).max(100).nullable(),
  memPct: z.number().min(0).max(100).nullable(),
  lastSeen: z.string().datetime().nullable(),
});
export type FsNode = z.infer<typeof FsNodeSchema>;

export const ClusterTopologyResponseSchema = z.object({
  nodes: z.array(FsNodeSchema),
  generatedAt: z.string().datetime(),
});
export type ClusterTopologyResponse = z.infer<typeof ClusterTopologyResponseSchema>;

export const CapacityGaugeSchema = z.object({
  nodeId: z.string().min(1),
  channelsActive: z.number().int().min(0),
  channelsLimit: z.number().int().min(0),
  saturationPct: z.number().min(0).max(100),
});
export type CapacityGauge = z.infer<typeof CapacityGaugeSchema>;

export const KpiScopeSchema = z.union([
  z.literal('cluster'),
  z.string().regex(/^fs:[A-Za-z0-9_-]+$/),
  z.string().regex(/^tenant:[A-Za-z0-9_-]+$/),
  z.string().regex(/^trunk:[A-Za-z0-9_-]+$/),
]);
export type KpiScope = z.infer<typeof KpiScopeSchema>;

export const PddPercentilesSchema = z.object({
  p50: z.number().min(0),
  p95: z.number().min(0),
  p99: z.number().min(0),
});
export type PddPercentiles = z.infer<typeof PddPercentilesSchema>;

export const MosStatsSchema = z.object({
  avg: z.number().min(0).max(5),
  p10: z.number().min(0).max(5),
});
export type MosStats = z.infer<typeof MosStatsSchema>;

export const CarrierKpisSchema = z.object({
  scope: KpiScopeSchema,
  callsAttempted: z.number().int().min(0),
  callsAnswered: z.number().int().min(0),
  asr: z.number().min(0).max(100),
  ner: z.number().min(0).max(100),
  acdSeconds: z.number().min(0),
  pdd: PddPercentilesSchema,
  mos: MosStatsSchema,
  jitterP95: z.number().min(0),
  packetLossPct: z.number().min(0).max(100),
  windowFrom: z.string().datetime(),
  windowTo: z.string().datetime(),
  trendVsPrev: z.enum(['up', 'down', 'flat']).optional(),
});
export type CarrierKpis = z.infer<typeof CarrierKpisSchema>;

export const PerTenantKpiRowSchema = z.object({
  tenantId: z.string().min(1),
  tenantName: z.string(),
  fsNodeId: z.string().nullable(),
  calls1h: z.number().int().min(0),
  asr: z.number().min(0).max(100),
  ner: z.number().min(0).max(100),
  acdSeconds: z.number().min(0),
  pddP95Ms: z.number().min(0),
  mosAvg: z.number().min(0).max(5),
  underThreshold: z.boolean(),
});
export type PerTenantKpiRow = z.infer<typeof PerTenantKpiRowSchema>;

export const AnomalySeveritySchema = z.enum(['warn', 'crit']);
export type AnomalySeverity = z.infer<typeof AnomalySeveritySchema>;

export const AnomalySourceKindSchema = z.enum(['cluster', 'fs', 'tenant', 'gateway', 'trunk', 'pipe', 'kamailio']);
export type AnomalySourceKind = z.infer<typeof AnomalySourceKindSchema>;

export const AnomalyItemSchema = z.object({
  id: z.string().min(1),
  ruleId: z.string().min(1),
  severity: AnomalySeveritySchema,
  sourceKind: AnomalySourceKindSchema,
  sourceId: z.string().min(1),
  metric: z.string().min(1),
  value: z.number(),
  threshold: z.number(),
  since: z.string().datetime(),
  lastSeen: z.string().datetime(),
  resolvedAt: z.string().datetime().nullable(),
  deepLink: z.string().min(1).nullable(),
});
export type AnomalyItem = z.infer<typeof AnomalyItemSchema>;

export const AnomalyRuleSchema = z.object({
  id: z.string().min(1),
  metric: z.string().min(1),
  warnThreshold: z.number(),
  critThreshold: z.number(),
  comparator: z.enum(['gt', 'lt', 'gte', 'lte']),
  minSamples: z.number().int().min(1).default(1),
  windowSec: z.number().int().min(5).default(60),
  scope: AnomalySourceKindSchema,
  description: z.string(),
});
export type AnomalyRule = z.infer<typeof AnomalyRuleSchema>;

export const DispatcherFlagSchema = z.enum(['AP', 'IP', 'DP', 'DX']);
export type DispatcherFlag = z.infer<typeof DispatcherFlagSchema>;

export const KamailioDispatcherDestinationSchema = z.object({
  setid: z.number().int().min(1),
  uri: z.string().min(1),
  flags: DispatcherFlagSchema,
  priority: z.number().int(),
  attrs: z.string(),
  probeLatencyMs: z.number().min(0).nullable(),
  lastStateChange: z.string().datetime().nullable(),
  failovers24h: z.number().int().min(0),
  forcedState: z.enum(['up', 'down']).nullable(),
});
export type KamailioDispatcherDestination = z.infer<typeof KamailioDispatcherDestinationSchema>;

export const TrunkFailoverEventSchema = z.object({
  id: z.string().min(1),
  trunkId: z.string().min(1),
  ts: z.string().datetime(),
  fromGateway: z.string().nullable(),
  toGateway: z.string().nullable(),
  reason: z.string(),
  callsAffected: z.number().int().min(0),
});
export type TrunkFailoverEvent = z.infer<typeof TrunkFailoverEventSchema>;

export const SipResponseMatrixCellSchema = z.object({
  nodeId: z.string().min(1),
  responseCode: z.number().int().min(100).max(699),
  count: z.number().int().min(0),
});
export type SipResponseMatrixCell = z.infer<typeof SipResponseMatrixCellSchema>;

export const RtpWorstCallSchema = z.object({
  callId: z.string().min(1),
  nodeId: z.string().min(1),
  jitterMs: z.number().min(0),
  packetLossPct: z.number().min(0).max(100),
  mos: z.number().min(0).max(5),
  durationSec: z.number().int().min(0),
  startedAt: z.string().datetime(),
});
export type RtpWorstCall = z.infer<typeof RtpWorstCallSchema>;

export const RegSecuritySnapshotSchema = z.object({
  totalRegistered: z.number().int().min(0),
  failedAttemptsLastHour: z.number().int().min(0),
  uniqueAttackerIps: z.number().int().min(0),
  topAttackers: z.array(z.object({
    ip: z.string(),
    attempts: z.number().int().min(0),
    countryCode: z.string().length(2).nullable(),
  })),
});
export type RegSecuritySnapshot = z.infer<typeof RegSecuritySnapshotSchema>;

export const TimeSeriesPointSchema = z.object({
  ts: z.string().datetime(),
  value: z.number(),
});
export type TimeSeriesPoint = z.infer<typeof TimeSeriesPointSchema>;

export const TimeSeriesWindowSchema = z.enum(['15m', '1h', '24h']);
export type TimeSeriesWindow = z.infer<typeof TimeSeriesWindowSchema>;

export const FsNodeSeriesKeySchema = z.enum([
  'active_channels',
  'cps',
  'registrations',
  'rtp_kbps_in',
  'rtp_kbps_out',
]);
export type FsNodeSeriesKey = z.infer<typeof FsNodeSeriesKeySchema>;

export const FsNodeTimeSeriesSchema = z.object({
  nodeId: z.string().min(1),
  window: TimeSeriesWindowSchema,
  sampleIntervalSec: z.number().int().min(1),
  series: z.record(FsNodeSeriesKeySchema, z.array(TimeSeriesPointSchema)),
});
export type FsNodeTimeSeries = z.infer<typeof FsNodeTimeSeriesSchema>;

export const KamailioSeriesKeySchema = z.enum([
  'sip_invite_per_sec',
  'sip_register_per_sec',
  'sip_options_per_sec',
  'sip_bye_per_sec',
  'sip_cancel_per_sec',
  'dispatcher_hit_per_sec',
  'dispatcher_fallback_per_sec',
  'dispatcher_dest_down_per_sec',
  'sync_duration_ms',
  'htable_entries',
  'auth_fail_per_sec',
]);
export type KamailioSeriesKey = z.infer<typeof KamailioSeriesKeySchema>;

export const KamailioTimeSeriesSchema = z.object({
  window: TimeSeriesWindowSchema,
  sampleIntervalSec: z.number().int().min(1),
  series: z.record(KamailioSeriesKeySchema, z.array(TimeSeriesPointSchema)),
  // guard anti-recidiva: distingue "sorgente non attiva" da "traffico
  // zero". `false` quando i counter `sbc:*` non sono pubblicati da Kamailio
  // (config counter non deployata / KAM non riavviato): le serie restano a zero ma
  // NON perché il traffico è nullo. Il FE mostra un avviso esplicito invece di
  // grafici piatti fuorvianti. `true` quando i counter esistono (anche a valore 0).
  sourceAvailable: z.boolean(),
});
export type KamailioTimeSeries = z.infer<typeof KamailioTimeSeriesSchema>;

export const PortSeriesKeySchema = z.enum([
  'connections',
  'bytes_in_per_sec',
  'bytes_out_per_sec',
]);
export type PortSeriesKey = z.infer<typeof PortSeriesKeySchema>;

export const PortTimeSeriesSchema = z.object({
  nodeId: z.string().min(1),
  port: z.number().int().min(1).max(65535),
  window: TimeSeriesWindowSchema,
  sampleIntervalSec: z.number().int().min(1),
  series: z.record(PortSeriesKeySchema, z.array(TimeSeriesPointSchema)),
});
export type PortTimeSeries = z.infer<typeof PortTimeSeriesSchema>;

export const NodeHealthSchema = z.object({
  nodeId: z.string().min(1),
  kind: z.enum(['fs', 'kamailio', 'redis', 'esl-gateway', 'haproxy']),
  status: z.enum(['green', 'yellow', 'red', 'unknown']),
  latencyMs: z.number().min(0).nullable(),
  uptimeSec: z.number().int().min(0).nullable(),
  lastFlapAt: z.string().datetime().nullable(),
  detail: z.string().nullable(),
});
export type NodeHealth = z.infer<typeof NodeHealthSchema>;

export const ClusterHealthStripSchema = z.object({
  generatedAt: z.string().datetime(),
  nodes: z.array(NodeHealthSchema),
});
export type ClusterHealthStrip = z.infer<typeof ClusterHealthStripSchema>;

export const TenantFsHeatmapCellSchema = z.object({
  tenantId: z.string().min(1),
  tenantName: z.string(),
  fsNodeId: z.string().min(1),
  callsLastHour: z.number().int().min(0),
});
export type TenantFsHeatmapCell = z.infer<typeof TenantFsHeatmapCellSchema>;

export const TenantFsMapRowSchema = z.object({
  tenantId: z.string().min(1),
  tenantName: z.string(),
  assignedFsNode: z.string().min(1),
  callsLive: z.number().int().min(0),
  registrations: z.number().int().min(0),
  lastCallAt: z.string().datetime().nullable(),
  manualOverride: z.boolean(),
});
export type TenantFsMapRow = z.infer<typeof TenantFsMapRowSchema>;

export const HashSkewEntrySchema = z.object({
  fsNodeId: z.string().min(1),
  actualPct: z.number().min(0).max(100),
  expectedPct: z.number().min(0).max(100),
  skewPct: z.number(),
});
export type HashSkewEntry = z.infer<typeof HashSkewEntrySchema>;

export const KamailioSyncHealthSchema = z.object({
  lastSuccessAt: z.string().datetime().nullable(),
  lastFailureAt: z.string().datetime().nullable(),
  consecutiveFailures: z.number().int().min(0),
  syncDurationP95Ms: z.number().min(0),
  htableCounts: z.record(z.string(), z.number().int().min(0)),
  hmacEnabled: z.boolean(),
  recentErrors: z.array(z.object({
    at: z.string().datetime(),
    target: z.string(),
    error: z.string(),
  })),
});
export type KamailioSyncHealth = z.infer<typeof KamailioSyncHealthSchema>;

// Contratto socket dell'event log NOC SIP Cluster (room
// `master:sip-cluster`, evento `cluster:event`). Vive in shared perché è la SoT
// condivisa tra l'emitter backend (observability/ops/internal feed) e il
// consumer FE (`useClusterRealtime` + `ClusterEventsLog`).
export const ClusterEventSchema = z.object({
  id: z.string().min(1),
  ts: z.string().datetime(),
  kind: z.enum(['failover', 'flap', 'sync', 'anomaly', 'action', 'security', 'info']),
  severity: z.enum(['info', 'warn', 'crit']),
  message: z.string(),
  source: z.string(),
});
export type ClusterEvent = z.infer<typeof ClusterEventSchema>;

export const NocActionLogEntrySchema = z.object({
  id: z.string(),
  ts: z.string().datetime(),
  actorEmail: z.string().email().nullable(),
  action: z.string().min(1),
  resourceType: z.string().min(1),
  resourceId: z.string(),
  status: z.enum(['ok', 'failed', 'dry_run', 'auto_revert']),
  durationMs: z.number().int().min(0).nullable(),
  dryRun: z.boolean(),
  reason: z.string().nullable(),
});
export type NocActionLogEntry = z.infer<typeof NocActionLogEntrySchema>;

// Payload broadcast Socket.IO `noc:error-tracking:issue` (room
// `master:sip-cluster`): issue error-tracking (GlitchTip) ricevuta via webhook.
export const ErrorTrackingIssueBroadcastSchema = z.object({
  issueId: z.string(),
  title: z.string(),
  level: z.string(),
  url: z.string().nullable(),
  project: z.string().nullable(),
  receivedAt: z.string().datetime(),
});
export type ErrorTrackingIssueBroadcast = z.infer<typeof ErrorTrackingIssueBroadcastSchema>;

// `SecfilterEntrySchema` rimosso (mod_secfilter dismesso, feature
// NOC ops secfilter + tabella `master.noc_secfilter_entries` eliminate).

// `RatelimitOverrideSchema`/`RatelimitAdjustRequestSchema` rimossi
// (mod_pipelimit dismesso). Endpoint `/security/ratelimit-overrides` e
// `/ops/ratelimit/adjust` eliminati nella stessa task.

export const NocOpsRequestSchema = z.object({
  reason: z.string().min(1, 'Motivazione obbligatoria'),
  dryRun: z.boolean().default(false),
  totpCode: z.string().regex(/^\d{6}$/).optional(),
  backupCode: z.string().min(8).max(32).optional(),
});
export type NocOpsRequest = z.infer<typeof NocOpsRequestSchema>;

export const DrainFsRequestSchema = NocOpsRequestSchema.extend({
  ttlMinutes: z.number().int().min(1).max(1440).nullable().default(null),
});
export type DrainFsRequest = z.infer<typeof DrainFsRequestSchema>;

// `SecfilterAddRequestSchema` rimosso (mod_secfilter dismesso).

export const RebalanceTenantRequestSchema = NocOpsRequestSchema.extend({
  targetFsNodeId: z.string().min(1),
});
export type RebalanceTenantRequest = z.infer<typeof RebalanceTenantRequestSchema>;

export const NocOpsResultSchema = z.object({
  ok: z.boolean(),
  dryRun: z.boolean(),
  actionId: z.string(),
  durationMs: z.number().int().min(0),
  affected: z.object({
    callsLive: z.number().int().min(0).optional(),
    tenants: z.number().int().min(0).optional(),
    registrations: z.number().int().min(0).optional(),
  }).optional(),
  message: z.string().optional(),
  rpcResponse: z.unknown().optional(),
});
export type NocOpsResult = z.infer<typeof NocOpsResultSchema>;

// ============================================================================
// Hub legal_entities + spoke financing_partners + pratiche
// ============================================================================
export const RoleTypeSchema = z.enum(['supplier', 'financing_partner', 'retail_brand', 'carrier']);
export type RoleType = z.infer<typeof RoleTypeSchema>;

// validatori fiscali allineati alla SoT condivisa
// (`validators/italian-fiscal.ts`): checksum P.IVA, formato CF, IBAN mod-97.
const ItalianVatLE = z.string().refine(isValidItalianVat, 'P.IVA italiana non valida (checksum)');
const ItalianCfLE = z.string().refine(isValidItalianFiscalCode, 'Codice fiscale non valido');
const IbanIT = z.string().refine(isValidIban, 'IBAN non valido (checksum)');
const SdiLE = z.string().refine(isValidSdiCode, 'Codice SDI non valido (6-7 alfanumerici)');
const PecLE = z.string().refine(isValidEmail, 'PEC non valida');
const EmailLE = z.string().refine(isValidEmail, 'Email non valida');

export const RegisteredAddressSchema = z.object({
  street: z.string().max(255).optional().nullable(),
  city: z.string().max(120).optional().nullable(),
  province: z.string().max(10).optional().nullable(),
  zip: z.string().max(20).optional().nullable(),
  country: z.string().max(60).optional().nullable(),
});
export type RegisteredAddress = z.infer<typeof RegisteredAddressSchema>;

export const LegalEntityInputSchema = z.object({
  legal_name: z.string().min(1, 'Ragione sociale obbligatoria').max(255),
  trade_name: z.string().max(255).optional().nullable(),
  vat_number: ItalianVatLE.optional().nullable(),
  tax_code: ItalianCfLE.optional().nullable(),
  sdi_code: SdiLE.optional().nullable(),
  legal_form: z.string().max(50).optional().nullable(),
  pec_email: PecLE.optional().nullable(),
  registered_address: RegisteredAddressSchema.optional().default({}),
  primary_iban: IbanIT.optional().nullable(),
  primary_bic: z.string().max(11).optional().nullable(),
  primary_phone: z.string().max(50).optional().nullable(),
  primary_email: EmailLE.optional().nullable(),
  website: z.string().max(255).optional().nullable(),
  notes: z.string().optional().nullable(),
  is_active: z.boolean().optional(),
});
export type LegalEntityInput = z.infer<typeof LegalEntityInputSchema>;

export interface LegalEntityRecord {
  id: string;
  tenant_id: string | null;
  legal_name: string;
  trade_name: string | null;
  vat_number: string | null;
  tax_code: string | null;
  sdi_code: string | null;
  legal_form: string | null;
  pec_email: string | null;
  registered_address: RegisteredAddress;
  primary_iban: string | null;
  primary_bic: string | null;
  primary_phone: string | null;
  primary_email: string | null;
  website: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  roles: Array<{ role_type: RoleType; is_active: boolean }>;
  contacts?: LegalEntityContactRecord[];
}

export const LegalEntityContactInputSchema = z.object({
  full_name: z.string().min(1, 'Nome referente obbligatorio').max(255),
  role_title: z.string().max(120).optional().nullable(),
  email: z.string().email().optional().nullable(),
  phone: z.string().max(50).optional().nullable(),
  mobile: z.string().max(50).optional().nullable(),
  notes: z.string().optional().nullable(),
  is_primary: z.boolean().optional(),
  sort_order: z.number().int().optional(),
});
export type LegalEntityContactInput = z.infer<typeof LegalEntityContactInputSchema>;

export interface LegalEntityContactRecord {
  id: string;
  tenant_id: string;
  legal_entity_id: string;
  full_name: string;
  role_title: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  notes: string | null;
  is_primary: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const LegalEntityCreateWithRolesSchema = LegalEntityInputSchema.extend({
  roles: z.array(RoleTypeSchema).optional().default([]),
  contacts: z.array(LegalEntityContactInputSchema).optional().default([]),
});
export type LegalEntityCreateWithRoles = z.infer<typeof LegalEntityCreateWithRolesSchema>;

export const FinancingPartnerInputSchema = z.object({
  legal_name: z.string().min(1).max(255),
  trade_name: z.string().max(255).optional().nullable(),
  vat_number: ItalianVatLE.optional().nullable(),
  tax_code: ItalianCfLE.optional().nullable(),
  sdi_code: SdiLE.optional().nullable(),
  legal_form: z.string().max(50).optional().nullable(),
  pec_email: PecLE.optional().nullable(),
  registered_address: RegisteredAddressSchema.optional().default({}),
  iban: IbanIT.optional().nullable(),
  bic: z.string().max(11).optional().nullable(),
  phone: z.string().max(50).optional().nullable(),
  email: EmailLE.optional().nullable(),
  website: z.string().max(255).optional().nullable(),
  notes: z.string().optional().nullable(),
  convention_code: z.string().max(80).optional().nullable(),
  recipient_company_id: z.string().uuid().optional().nullable(),
  commission_percent: z.number().min(0).max(100).optional(),
  expected_settlement_days: z.number().int().min(0).max(365).optional(),
  logo_url: z.string().max(500).optional().nullable(),
  color: z.string().max(20).optional().nullable(),
  is_active: z.boolean().optional(),
  sort_order: z.number().int().optional(),
});
export type FinancingPartnerInput = z.infer<typeof FinancingPartnerInputSchema>;

export const FinancingContractStatusSchema = z.enum([
  'pending', 'approved', 'rejected', 'perfected', 'cancelled', 'settled',
]);
export type FinancingContractStatus = z.infer<typeof FinancingContractStatusSchema>;

export const FinancingContractInputSchema = z.object({
  financing_partner_id: z.string().uuid(),
  sale_header_id: z.string().uuid().optional().nullable(),
  invoice_id: z.string().uuid().optional().nullable(),
  company_id: z.string().uuid(),
  customer_id: z.string().uuid().optional().nullable(),
  person_id: z.string().uuid().optional().nullable(),
  financed_amount: z.number().min(0),
  customer_advance: z.number().min(0).optional(),
  installments_count: z.number().int().min(1),
  tan_percent: z.number().min(0).max(100).optional().nullable(),
  taeg_percent: z.number().min(0).max(100).optional().nullable(),
  monthly_installment: z.number().min(0).optional().nullable(),
  partner_contract_code: z.string().max(120).optional().nullable(),
  cir: z.string().max(120).optional().nullable(),
  notes: z.string().optional().nullable(),
});
export type FinancingContractInput = z.infer<typeof FinancingContractInputSchema>;

// Info aggiuntive metodi di pagamento (`pos_payment_methods.config`).
// Blocco `extra_info.financing`: flag di attivazione + tri-stato per campo
// (obbligatorio/opzionale/non richiesto). Schema aperto ad altri blocchi futuri
// (buoni pasto ecc.) senza cambi di colonna.
export const ExtraInfoFieldRequirementSchema = z.enum(['required', 'optional', 'off']);
export type ExtraInfoFieldRequirement = z.infer<typeof ExtraInfoFieldRequirementSchema>;

// campi configurabili: entra `monthly_installment` (importo rata,
// colonna esistente in `financing_contracts`), esce `customer_advance` dal
// tri-stato: l'anticipo NON si ridigita in cassa, è ereditato dall'entry fee
// del componente prodotto del listino e popola `financing_contracts.customer_advance`.
export const FinancingExtraInfoFieldsSchema = z.object({
  financing_partner: ExtraInfoFieldRequirementSchema.default('off'),
  cir: ExtraInfoFieldRequirementSchema.default('off'),
  installments_count: ExtraInfoFieldRequirementSchema.default('off'),
  monthly_installment: ExtraInfoFieldRequirementSchema.default('off'),
});
export type FinancingExtraInfoFields = z.infer<typeof FinancingExtraInfoFieldsSchema>;

export const FinancingExtraInfoBlockSchema = z.object({
  enabled: z.boolean().default(false),
  fields: FinancingExtraInfoFieldsSchema.default({
    financing_partner: 'off', cir: 'off', installments_count: 'off', monthly_installment: 'off',
  }),
});
export type FinancingExtraInfoBlock = z.infer<typeof FinancingExtraInfoBlockSchema>;

export const PaymentMethodConfigSchema = z.object({
  extra_info: z.object({
    financing: FinancingExtraInfoBlockSchema.optional(),
    // tra più metodi attivi con gli stessi codici RT/fattura
    // della modalità all'insegna, questo è quello usato per la quota
    // finanziata (disambiguazione del metodo derivato; 0 o >1 marcati = 422).
    use_for_financed_quota: z.boolean().optional(),
  }).optional(),
});

/** TRUE se il metodo è marcato per le quote finanziate. */
export function isUseForFinancedQuota(config: PaymentMethodConfig | null | undefined): boolean {
  return config?.extra_info?.use_for_financed_quota === true;
}
export type PaymentMethodConfig = z.infer<typeof PaymentMethodConfigSchema>;

/** True se il metodo ha il blocco Finanziamento attivo (sostituisce l'euristica 'finanz'). */
export function isFinancingConfigEnabled(config: PaymentMethodConfig | null | undefined): boolean {
  return config?.extra_info?.financing?.enabled === true;
}

/** Campi del blocco Finanziamento del metodo (default tutti 'off'). */
export function getFinancingConfigFields(config: PaymentMethodConfig | null | undefined): FinancingExtraInfoFields {
  return config?.extra_info?.financing?.fields ?? {
    financing_partner: 'off', cir: 'off', installments_count: 'off', monthly_installment: 'off',
  };
}

// dati pratica finanziamento raccolti in cassa alla vendita
// (popup sul metodo con blocco attivo); il backend crea `financing_contracts`
// in stato 'approved' in-tx con la vendita.
export const SaleFinancingInputSchema = z.object({
  payment_method_id: z.string().uuid(),
  financing_partner_id: z.string().uuid().optional().nullable(),
  cir: z.string().trim().max(120).optional().nullable(),
  installments_count: z.number().int().min(1).max(240).optional().nullable(),
  // importo rata dal popup cassa; l'anticipo cliente NON è più
  // input: il backend lo eredita dall'entry fee del componente di composizione.
  monthly_installment: z.number().min(0).optional().nullable(),
});
export type SaleFinancingInput = z.infer<typeof SaleFinancingInputSchema>;

export const FinancingContractStatusChangeSchema = z.object({
  to_status: z.enum(['approved', 'rejected', 'perfected', 'cancelled']),
  cir: z.string().max(120).optional().nullable(),
  reason: z.string().max(500).optional().nullable(),
});
export type FinancingContractStatusChange = z.infer<typeof FinancingContractStatusChangeSchema>;

export const FinancingSettlementInputSchema = z.object({
  financing_partner_id: z.string().uuid(),
  company_id: z.string().uuid(),
  value_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato data YYYY-MM-DD'),
  gross_amount: z.number().min(0),
  commission_amount: z.number().min(0).optional(),
  recipient_iban: z.string().max(34).optional().nullable(),
  cro_reference: z.string().max(80).optional().nullable(),
  causale: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  contract_ids: z.array(z.string().uuid()).min(1, 'Almeno una pratica'),
});
export type FinancingSettlementInput = z.infer<typeof FinancingSettlementInputSchema>;

// ===========================================================================
// Abbina lead a vendita (riusabile InvoiceModal + Cassa)
// ===========================================================================

/**
 * Richiesta di ricerca dei lead aperti che combaciano col cessionario del
 * documento di vendita. L'identità è ri-risolta SERVER-SIDE da questi id (mai
 * fidarsi di identificatori grezzi dal client).
 */
export const LeadSaleMatchSearchSchema = z
  .object({
    customerId: z.string().uuid().optional().nullable(),
    personId: z.string().uuid().optional().nullable(),
  })
  .refine((v) => !!v.customerId || !!v.personId, {
    message: 'Serve almeno customerId o personId',
  });
export type LeadSaleMatchSearchRequest = z.infer<typeof LeadSaleMatchSearchSchema>;

/** Singolo lead candidato all'abbinamento con la vendita. */
export interface LeadSaleMatchLead {
  lead_id: string;
  lead_name: string;
  campaign_id: string;
  campaign_name: string;
  stage_id: string;
  stage_name: string;
  stage_type: string;
  lead_type: string | null;
  value: number | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  created_at: string;
  /** Criteri su cui il lead ha combaciato (es. 'person','email','phone'). */
  matched_on: string[];
}

export interface LeadSaleMatchSearchResponse {
  leads: LeadSaleMatchLead[];
}

// ---------------------------------------------------------------------------
// Meta Business (Facebook/Instagram) — connessioni e Pagine per-tenant
// ---------------------------------------------------------------------------

/** Stato di una connessione Meta. `permission_error` = realign fallito per
 *  errori permessi Meta (es. #200) con token ancora valido: distinto da
 *  `expired` (token non più rinnovabile) e auto-recuperabile al realign. */
export type MetaConnectionStatusValue =
  | 'connected'
  | 'expired'
  | 'error'
  | 'disconnected'
  | 'permission_error';

/** Connessione Meta (token utente long-lived), account-level. Niente token. */
export interface MetaConnectionStatus {
  id: string;
  connectedByUserId: string;
  storeId: string | null;
  status: MetaConnectionStatusValue;
  /** Etichetta personalizzata per distinguere account omonimi. `null` = nessuna. */
  displayLabel: string | null;
  grantedScopes: string[];
  tokenExpiresAt: string | null;
  metaUserName: string | null;
  businessId: string | null;
  adAccountId: string | null;
  lastRefreshAt: string | null;
  lastRefreshError: string | null;
  connectedAt: string;
}

/** Singola Pagina Meta connessa (sorgente chat clienti). Niente token. */
/** Stato di sottoscrizione webhook di una Pagina, verificato live su Graph
 *  (`GET /{pageId}/subscribed_apps`). Senza la sottoscrizione al campo
 *  `messages` la Pagina NON riceve i messaggi inbound Messenger. */
export interface MetaPageWebhookStatus {
  /** true se la Pagina è sottoscritta a TUTTI i campi attesi (incluso `messages`). */
  subscribed: boolean;
  /** Campi webhook attesi non ancora sottoscritti (es. `['messages']`). */
  missingFields: string[];
  /** Errore di verifica (token assente, Graph non raggiungibile). `null` se ok. */
  error: string | null;
}

export interface MetaPageStatus {
  id: string;
  /** Connessioni che amministrano la Pagina (giunzione N:N, più recente prima).
   *  Vuoto = pagina orfana: nessuna connessione la amministra più (preservata
   *  per lo storico conversazioni). */
  connectionIds: string[];
  pageId: string;
  pageName: string | null;
  businessId: string | null;
  businessName: string | null;
  instagramBusinessAccountId: string | null;
  instagramUsername: string | null;
  chatEnabled: boolean;
  /** Valorizzato quando la Pagina non è più amministrata dalla connessione ma
   *  è preservata per lo storico conversazioni (chat forzata off). `null` = attiva. */
  removedAt: string | null;
  /** Stato sottoscrizione webhook verificato live. `null` quando non verificabile
   *  (app Meta non configurata, Pagina rimossa o senza page access token). */
  webhook: MetaPageWebhookStatus | null;
}

/** Risposta di `GET /api/meta/status`. */
export interface MetaStatusResponse {
  meta_app_available: boolean;
  current_user_id: string;
  connections: MetaConnectionStatus[];
  pages: MetaPageStatus[];
}

/** Esito della (ri)sottoscrizione webhook di una singola Pagina. */
export interface MetaPageSubscribeResult {
  pageId: string;
  pageName: string | null;
  /** true se la Pagina risulta sottoscritta ai campi attesi dopo l'azione. */
  subscribed: boolean;
  /** Campi attesi non sottoscritti in caso di fallimento. Vuoto se `subscribed`. */
  missingFields: string[];
  /** Messaggio d'errore della sottoscrizione. `null` se riuscita. */
  error: string | null;
}

/** Esito del riallineamento Pagine/scope di una connessione Meta. */
export interface MetaConnectionRealign {
  connectionId: string;
  metaUserName: string | null;
  status: 'ok' | 'error';
  grantedScopes: string[];
  pagesTotal: number;
  pagesAdded: number;
  pagesUpdated: number;
  pagesDeleted: number;
  pagesMarkedRemoved: number;
  /** Esito della (ri)sottoscrizione webhook per ciascuna Pagina della connessione. */
  pageSubscriptions: MetaPageSubscribeResult[];
  error: string | null;
}

/** Risposta di `POST /api/meta/realign` (tutte le connessioni) e di
 *  `POST /api/meta/connections/:connectionId/realign` (singola). */
export interface MetaRealignResponse {
  connectionsChecked: number;
  connectionsOk: number;
  connectionsError: number;
  results: MetaConnectionRealign[];
}

/** Risposta di `POST /api/meta/connections/:connectionId/refresh-token`. */
export interface MetaConnectionRefreshTokenResponse {
  connectionId: string;
  status: 'refreshed' | 'failed';
  /** Nuova scadenza del token long-lived; `null` se Meta non la riporta. */
  tokenExpiresAt: string | null;
  error: string | null;
}

/** Risposta di `DELETE /api/meta/connections/:connectionId`. */
export interface MetaConnectionDeleteResponse {
  connectionId: string;
  /** Pagine eliminate (senza conversazioni). */
  pagesDeleted: number;
  /** Pagine soft-markate (`removed_at`) perché con conversazioni esistenti. */
  pagesMarkedRemoved: number;
}

/** Risposta di `PATCH /api/meta/connections/:connectionId`. */
export interface MetaConnectionLabelResponse {
  connectionId: string;
  displayLabel: string | null;
}

// ==================== POS — ESITO EMISSIONE SCONTRINO (server-side) ====================
// L'emissione dello scontrino RT avviene lato backend dentro `POST /pos/sales`
// (sync con auto-accodamento su errore ritentabile). La risposta della vendita
// allega questo esito in `fiscal`; il frontend si limita a mostrare l'esito e,
// per le sole stampanti `websocket` (raggiungibili solo dalla LAN del browser),
// a eseguire la gamba di trasporto (`build-command`/`report-result`).
export interface PosSaleFiscalOutcome {
  /** Stampante risolta per l'emissione (assente se `skipped`). */
  printerId?: string;
  /** `websocket` = il backend non può raggiungere la RT: il browser esegue il trasporto. */
  transport?: 'websocket';
  /** Emissione non tentata: nessuna stampante risolta o nessuna riga fiscale. */
  skipped?: 'no_printer' | 'no_fiscal_lines';
  success?: boolean;
  receiptNumber?: string;
  errorCode?: string;
  errorMessage?: string;
  errorCategory?: string;
  /** Errore ritentabile (rete/timeout). */
  canRetryInQueue?: boolean;
  /** Il comando è stato auto-accodato in `pos_fiscal_print_queue`. */
  autoQueued?: boolean;
}

// ==================== POS — QUADRATURA CHIUSURA GIORNALIERA ====================
// Gamba "sistema" della quadratura di una chiusura: aggregati calcolati dal
// gestionale (vendite/pagamenti/documenti emessi) da confrontare con i valori
// dichiarati dalla RT (report Z) o da A-Cube. Endpoint:
// GET /pos/daily-closings/:id/quadratura

export interface PosQuadraturaVatRow {
  /** Aliquota percentuale (es. 22). */
  aliquota: number;
  imponibile: number;
  imposta: number;
  totale: number;
}

export interface PosQuadraturaPaymentRow {
  payment_method_id: string | null;
  payment_method_name: string;
  /** Codice AdE del metodo (es. '5' = non riscosso/omaggio), se disponibile. */
  ade_code: string | null;
  amount: number;
  transactions: number;
}

export interface PosQuadraturaNumbering {
  /** Numerazione analizzata solo per chiusure fisiche (fiscal_receipt_number). */
  available: boolean;
  first_number: number | null;
  last_number: number | null;
  documents_count: number;
  /** Numeri mancanti nella sequenza first..last (max 50 riportati). */
  missing_numbers: number[];
  missing_truncated: boolean;
}

export interface PosQuadraturaResponse {
  closing_id: string;
  closing_date: string;
  source: 'physical' | 'digital';
  fiscal_printer_id: string | null;
  /** Totale dichiarato dalla chiusura (Z o A-Cube). */
  declared_total: number;
  /** Totale sistema: somma vendite non-draft con documento fiscale del giorno. */
  system_total: number;
  delta: number;
  vat_rows: PosQuadraturaVatRow[];
  vat_total: { imponibile: number; imposta: number; totale: number };
  payment_rows: PosQuadraturaPaymentRow[];
  payments_total: number;
  /** Vendite del giorno con Σ pagamenti ≠ totale vendita (id + delta). */
  payment_mismatches: Array<{ sale_id: string; sale_number: string | null; sale_total: number; payments_total: number; delta: number }>;
  numbering: PosQuadraturaNumbering;
}
